import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, Play, Pause, Save, Wand2, Download, 
  Image, Layers, Mic, Music, Trash2, Volume2, Film,
  MessageSquare, Zap, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff,
  User, Clapperboard, Square, Edit3, Check, X, Upload, Box, 
  Target, Search, DollarSign, FolderInput, Maximize2, ZoomIn, ZoomOut,
  RotateCw, Loader2, PanelLeftClose, PanelLeft, Palette, FlipHorizontal, FlipVertical, Users,
  Lightbulb, Grid, Printer, FastForward, Star, Copy
} from "lucide-react";
import VideoMouthPosition from "../components/VideoMouthPosition";
import { 
  PipelineHeader, 
  TakesCarousel,
  StrategyStage,
  PreviewStage,
  AnimateStage,
  LipsyncStage
} from "../components/studio";
import { API, getAssetUrl, createNewCharacter, createNewAudioTrack, INITIAL_ASSET_LIMIT } from "@/utils/studioUtils";
import SmartImportModal from "@/components/SmartImportModal";
import { uploadFileToS3, uploadBlobToS3 } from "@/utils/s3Upload";
import { VoiceRecorder } from "@/components/studio/VoiceRecorder";

// Import custom hooks for state management
import { useAssets } from "./AnimationStudio/hooks/useAssets";
import { useLipsync } from "./AnimationStudio/hooks/useLipsync";

// Import extracted components
import { PrintStage } from "./AnimationStudio/components";

// DraggableProperty component - extracted to avoid re-creation on each render
const DraggableProperty = ({ 
  label, 
  value, 
  index, 
  property, 
  format = (v) => v, 
  unit = "", 
  icon = null,
  editingProperty,
  setEditingProperty,
  onPropertyDragStart,
  onPropertyChange
}) => {
  const isEditing = editingProperty?.index === index && editingProperty?.property === property;
  const [inputValue, setInputValue] = useState(format(value));

  useEffect(() => {
    if (!isEditing) {
      setInputValue(format(value));
    }
  }, [value, isEditing, format]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingProperty({ index, property });
    setInputValue(format(value));
  };

  const handleBlur = () => {
    let num = parseFloat(inputValue);
    if (!isNaN(num)) {
      if (property === 'scale') {
        num = num / 100;
      }
      onPropertyChange(index, property, num);
    }
    setEditingProperty(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setEditingProperty(null);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-0.5 min-w-0" onClick={(e) => e.stopPropagation()}>
        {icon && <span className="text-[10px] text-zinc-500">{icon}</span>}
        <span className="text-[10px] text-zinc-500 w-5 shrink-0">{label}</span>
        <input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-0 bg-zinc-900 border border-indigo-500 rounded px-1 py-0.5 text-[10px] text-white text-center"
          autoFocus
          onClick={(e) => e.stopPropagation()}
        />
        <span className="text-[10px] text-zinc-500 shrink-0">{unit}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5 min-w-0">
      {icon && <span className="text-[10px] text-zinc-500">{icon}</span>}
      <span className="text-[10px] text-zinc-500 w-5 shrink-0">{label}</span>
      <span
        onMouseDown={(e) => onPropertyDragStart(e, index, property)}
        onDoubleClick={handleDoubleClick}
        className="flex-1 min-w-0 bg-zinc-800 rounded px-1 py-0.5 text-[10px] text-indigo-400 text-center cursor-move hover:bg-zinc-700 select-none truncate"
        title="Drag ↔ or ↕ to adjust, double-click to type"
      >
        {format(value)}{unit}
      </span>
    </div>
  );
};

// LayerItem component - extracted to avoid re-creation on each render
const LayerItem = ({ 
  char, 
  index, 
  isSelected, 
  isModal = false,
  activeCharactersLength,
  editingLayerName,
  setEditingLayerName,
  editingProperty,
  setEditingProperty,
  onSelect,
  onNameChange,
  onVisibilityToggle,
  onMoveUp,
  onMoveDown,
  onRemove,
  onFlip,
  onPropertyDragStart,
  onPropertyChange
}) => {
  const isEditingName = editingLayerName === index;
  const [nameValue, setNameValue] = useState(char.character_name || char.name);

  return (
    <div
      onClick={() => onSelect(isSelected ? null : index)}
      className={`rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'bg-indigo-500/20 border border-indigo-500/50' 
          : 'bg-zinc-800/50 border border-transparent hover:bg-zinc-800'
      }`}
    >
      <div className="flex items-center gap-2 p-2">
        <div className="w-8 h-8 rounded bg-zinc-700 overflow-hidden flex-shrink-0">
          <img src={getAssetUrl(char.file_url)} alt={char.name} className="w-full h-full object-cover" />
        </div>
        
        {isEditingName ? (
          <input
            type="text"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => {
              onNameChange(index, nameValue);
              setEditingLayerName(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onNameChange(index, nameValue);
                setEditingLayerName(null);
              }
            }}
            className="flex-1 min-w-0 bg-zinc-900 border border-indigo-500 rounded px-2 py-0.5 text-xs text-white uppercase"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span 
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditingLayerName(index);
              setNameValue(char.character_name || char.name);
            }}
            className={`text-xs flex-1 min-w-0 truncate ${char.visible !== false ? 'text-zinc-200' : 'text-zinc-500'}`}
            title={char.character_name || char.name}
          >
            {(() => {
              const name = char.character_name || char.name || '';
              // Remove file extension and truncate to 12 chars
              const cleanName = name.replace(/\.(png|jpg|jpeg|gif|webp)$/i, '');
              return cleanName.length > 12 ? cleanName.slice(0, 12) + '...' : cleanName;
            })()}
          </span>
        )}
        
        <div className="flex items-center gap-0.5">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={(e) => { e.stopPropagation(); onVisibilityToggle(index); }} 
            className="w-6 h-6 p-0 text-zinc-400 hover:text-white"
          >
            {char.visible !== false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={(e) => { e.stopPropagation(); onMoveUp(index); }} 
            disabled={index === 0} 
            className="w-6 h-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
          >
            <ChevronUp className="w-3 h-3" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={(e) => { e.stopPropagation(); onMoveDown(index); }} 
            disabled={index === activeCharactersLength - 1} 
            className="w-6 h-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
          >
            <ChevronDown className="w-3 h-3" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={(e) => { e.stopPropagation(); onRemove(index); }} 
            className="w-6 h-6 p-0 text-zinc-400 hover:text-red-400"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
      
      {/* Expanded settings when selected */}
      {isSelected && (
        <div className="px-2 pb-2 pt-1 border-t border-zinc-700/50 mt-1">
          <div className={`grid ${isModal ? 'grid-cols-6' : 'grid-cols-2'} gap-1`}>
            <DraggableProperty label="Scale" value={char.scale} index={index} property="scale" format={(v) => (v * 100).toFixed(0)} unit="%" editingProperty={editingProperty} setEditingProperty={setEditingProperty} onPropertyDragStart={onPropertyDragStart} onPropertyChange={onPropertyChange} />
            <DraggableProperty label="Rot" value={char.rotation ?? 0} index={index} property="rotation" format={(v) => Math.round(v)} unit="°" editingProperty={editingProperty} setEditingProperty={setEditingProperty} onPropertyDragStart={onPropertyDragStart} onPropertyChange={onPropertyChange} />
            <DraggableProperty label="Alpha" value={char.opacity ?? 100} index={index} property="opacity" format={(v) => Math.round(v)} unit="%" editingProperty={editingProperty} setEditingProperty={setEditingProperty} onPropertyDragStart={onPropertyDragStart} onPropertyChange={onPropertyChange} />
            <DraggableProperty label="Blur" value={char.blur ?? 0} index={index} property="blur" format={(v) => Math.round(v)} unit="px" editingProperty={editingProperty} setEditingProperty={setEditingProperty} onPropertyDragStart={onPropertyDragStart} onPropertyChange={onPropertyChange} />
            <DraggableProperty label="X" value={char.x ?? 50} index={index} property="x" format={(v) => Math.round(v || 0)} unit="%" icon="↔" editingProperty={editingProperty} setEditingProperty={setEditingProperty} onPropertyDragStart={onPropertyDragStart} onPropertyChange={onPropertyChange} />
            <DraggableProperty label="Y" value={char.y ?? 50} index={index} property="y" format={(v) => Math.round(v || 0)} unit="%" icon="↕" editingProperty={editingProperty} setEditingProperty={setEditingProperty} onPropertyDragStart={onPropertyDragStart} onPropertyChange={onPropertyChange} />
          </div>
          {/* Flip Controls */}
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-zinc-700/30">
            <span className="text-[10px] text-zinc-500 uppercase">Flip:</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onFlip(index, 'horizontal'); }}
              className={`h-6 px-2 gap-1 text-xs ${char.flipX ? 'bg-indigo-500/30 text-indigo-300' : 'text-zinc-400 hover:text-white'}`}
              title="Flip Horizontal"
            >
              <FlipHorizontal className="w-3 h-3" />
              <span>H</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onFlip(index, 'vertical'); }}
              className={`h-6 px-2 gap-1 text-xs ${char.flipY ? 'bg-indigo-500/30 text-indigo-300' : 'text-zinc-400 hover:text-white'}`}
              title="Flip Vertical"
            >
              <FlipVertical className="w-3 h-3" />
              <span>V</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// AssetPanel component - extracted to avoid re-creation on each render
const AssetPanel = ({ 
  type, 
  label, 
  icon: Icon, 
  acceptType, 
  aspectRatio = "square", 
  onAdd,
  scene,
  assets,
  assetSearch,
  setAssetSearch,
  selectedBackground,
  setSelectedBackground,
  expandedAssetTypes,
  toggleAssetExpanded,
  uploadingType,
  handleUploadAsset,
  handleOpenImport,
  handleCharacterAdd,
  handleAudioAdd,
  // New props for character profile folder view
  characterProfiles = [],
  selectedCharacterProfile,
  setSelectedCharacterProfile,
  onBulkUpload
}) => {
  const getAssetsByType = (assetType) => {
    let filtered = assets.filter(a => a.type === assetType);
    if (assetSearch.trim()) {
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(assetSearch.toLowerCase())
      );
    }
    return filtered;
  };

  const getSuggestedAssets = (assetType) => {
    if (!scene) return [];
    const allAssets = getAssetsByType(assetType);
    
    if (assetType === 'background') {
      const suggestedLocation = scene.suggested_location || '';
      if (!suggestedLocation) return [];
      const locationLower = suggestedLocation.toLowerCase();
      return allAssets.filter(asset => {
        const nameLower = asset.name.toLowerCase();
        return nameLower.includes(locationLower) || 
               locationLower.includes(nameLower) ||
               locationLower.split(/[\s_-]+/).some(word => word.length > 2 && nameLower.includes(word)) ||
               nameLower.split(/[\s_-]+/).some(word => word.length > 2 && locationLower.includes(word));
      });
    } else if (assetType === 'character') {
      // For characters, match against character profiles instead of assets
      const suggestedCharacters = scene.suggested_characters || [];
      if (suggestedCharacters.length === 0) return [];
      return characterProfiles.filter(profile => {
        const nameLower = profile.name.toLowerCase();
        return suggestedCharacters.some(char => {
          const charLower = char.toLowerCase();
          return nameLower.includes(charLower) || 
                 charLower.includes(nameLower) ||
                 charLower.split(/[\s_-]+/).some(word => word.length > 2 && nameLower.includes(word)) ||
                 nameLower.split(/[\s_-]+/).some(word => word.length > 2 && charLower.includes(word));
        });
      });
    } else if (assetType === 'prop') {
      // For props/items, match against suggested_props from scene
      const suggestedProps = scene.suggested_props || [];
      if (suggestedProps.length === 0) return [];
      return allAssets.filter(asset => {
        const nameLower = asset.name.toLowerCase();
        return suggestedProps.some(prop => {
          const propLower = prop.toLowerCase();
          return nameLower.includes(propLower) || 
                 propLower.includes(nameLower) ||
                 propLower.split(/[\s_-]+/).some(word => word.length > 2 && nameLower.includes(word)) ||
                 nameLower.split(/[\s_-]+/).some(word => word.length > 2 && propLower.includes(word));
        });
      });
    }
    return [];
  };

  // Get primary image from character profile
  const getPrimaryImage = (profile) => {
    const primary = profile.reference_images?.find(img => img.is_primary);
    return primary || profile.reference_images?.[0];
  };

  const getAssetThumbnail = (asset) => {
    return asset.thumbnail_url ? getAssetUrl(asset.thumbnail_url) : getAssetUrl(asset.file_url);
  };

  const suggestedAssets = getSuggestedAssets(type);
  const allAssets = getAssetsByType(type);
  const suggestedIds = new Set(suggestedAssets.map(a => a.id));
  const otherAssets = allAssets.filter(a => !suggestedIds.has(a.id));
  
  const isExpanded = expandedAssetTypes[type] || false;
  const isSearching = assetSearch.trim().length > 0;
  const visibleOtherAssets = isSearching 
    ? allAssets
    : isExpanded 
      ? otherAssets 
      : otherAssets.slice(0, INITIAL_ASSET_LIMIT);
  const hasMore = !isSearching && !isExpanded && otherAssets.length > INITIAL_ASSET_LIMIT;
  const remainingCount = otherAssets.length - INITIAL_ASSET_LIMIT;

  const handleAssetClick = (asset) => {
    if (onAdd) {
      onAdd(asset);
    } else if (type === 'background') {
      setSelectedBackground(asset.id);
    } else if (type === 'voice' || type === 'music') {
      handleAudioAdd(asset);
    } else {
      handleCharacterAdd(asset);
    }
  };
  
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
        <Input
          placeholder={`Search ${label.toLowerCase()}...`}
          value={assetSearch}
          onChange={(e) => setAssetSearch(e.target.value)}
          className="pl-7 bg-zinc-800 border-zinc-700 text-white text-xs h-7"
        />
      </div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-zinc-500">{label} <span className="text-zinc-600">({allAssets.length})</span></p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleOpenImport(type)}
            className="w-6 h-6 p-0 text-zinc-400 hover:text-blue-400"
            title="Import from another project"
          >
            <FolderInput className="w-3 h-3" />
          </Button>
          <label className="cursor-pointer">
            <input
              type="file"
              accept={acceptType}
              multiple={type === 'character'}
              className="hidden"
              onChange={(e) => {
                if (type === 'character' && onBulkUpload) {
                  onBulkUpload(Array.from(e.target.files || []));
                  e.target.value = "";
                } else {
                  handleUploadAsset(e.target.files[0], type);
                }
              }}
            />
            <div className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white">
              <Upload className="w-3 h-3" />
            </div>
          </label>
        </div>
      </div>
      <ScrollArea className="flex-1">
        {/* Character Profile Folder View */}
        {type === 'character' ? (
          selectedCharacterProfile ? (
            // Inside a character profile - show poses
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCharacterProfile(null)}
                className="mb-2 text-zinc-400 hover:text-white -ml-2 h-6 text-xs"
              >
                <ChevronLeft className="w-3 h-3 mr-1" />
                Back
              </Button>
              <div className="text-xs font-medium text-white mb-2">{selectedCharacterProfile.name}</div>
              {selectedCharacterProfile.reference_images?.length === 0 ? (
                <div className="text-center py-4 text-zinc-500 text-[10px]">
                  No images in this character
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {selectedCharacterProfile.reference_images?.map((img, index) => {
                    const asset = assets.find(a => a.id === img.asset_id);
                    if (!asset) return null;
                    return (
                      <div
                        key={img.asset_id}
                        onClick={() => handleCharacterAdd(asset)}
                        className={`relative group aspect-square rounded-lg bg-zinc-800 overflow-hidden cursor-pointer border ${img.is_primary ? 'border-amber-500' : 'border-zinc-700 hover:border-indigo-500'}`}
                      >
                        <img
                          src={getAssetUrl(img.file_url || asset.file_url)}
                          alt={asset.name}
                          className="w-full h-full object-cover"
                        />
                        {img.is_primary && (
                          <div className="absolute top-0.5 left-0.5 bg-amber-500 text-black text-[8px] px-1 rounded font-medium">
                            Primary
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 pt-3">
                          <span className="text-[10px] text-white truncate block">{asset.name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // Character profiles list (folder view)
            <>
              {/* Suggested Characters */}
              {suggestedAssets.length > 0 && !isSearching && (
                <div className="mb-3 p-2">
                  <p className="text-[10px] text-amber-400 font-medium mb-1.5 flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Suggested for this shot
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {suggestedAssets.map((profile) => {
                      const primaryImage = getPrimaryImage(profile);
                      return (
                        <div
                          key={profile.id + '-suggested'}
                          onClick={() => setSelectedCharacterProfile(profile)}
                          className="relative group aspect-square rounded-lg bg-amber-500/10 border border-amber-500/30 overflow-hidden cursor-pointer hover:border-amber-400"
                        >
                          {primaryImage ? (
                            <img
                              src={getAssetUrl(primaryImage.thumbnail_url || primaryImage.file_url)}
                              alt={profile.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Users className="w-6 h-6 text-zinc-600" />
                            </div>
                          )}
                          <div className="absolute top-0.5 right-0.5 bg-black/60 text-zinc-300 text-[8px] px-1 rounded flex items-center gap-0.5">
                            <Layers className="w-2 h-2" />
                            {profile.reference_images?.length || 0}
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 pt-3">
                            <span className="text-[10px] text-white truncate block">{profile.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* All Character Profiles */}
              <div className="p-2">
                {characterProfiles.length === 0 ? (
                  <div className="text-center py-4 text-zinc-500 text-[10px]">
                    {assetSearch ? `No characters matching "${assetSearch}"` : "No characters uploaded"}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {characterProfiles
                      .filter(profile => !assetSearch || profile.name.toLowerCase().includes(assetSearch.toLowerCase()))
                      .map((profile) => {
                        const primaryImage = getPrimaryImage(profile);
                        return (
                          <div
                            key={profile.id}
                            onClick={() => setSelectedCharacterProfile(profile)}
                            className="relative group aspect-square rounded-lg bg-zinc-800 overflow-hidden cursor-pointer border border-zinc-700 hover:border-indigo-500"
                          >
                            {primaryImage ? (
                              <img
                                src={getAssetUrl(primaryImage.thumbnail_url || primaryImage.file_url)}
                                alt={profile.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Users className="w-6 h-6 text-zinc-600" />
                              </div>
                            )}
                            <div className="absolute top-0.5 right-0.5 bg-black/60 text-zinc-300 text-[8px] px-1 rounded flex items-center gap-0.5">
                              <Layers className="w-2 h-2" />
                              {profile.reference_images?.length || 0}
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 pt-3">
                              <span className="text-[10px] text-white truncate block">{profile.name}</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </>
          )
        ) : (
          // Non-character assets - original display
          <>
        {/* Suggested Section */}
        {suggestedAssets.length > 0 && !isSearching && (
          <div className="mb-3">
            <p className="text-[10px] text-amber-400 font-medium mb-1.5 flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Suggested for this shot
            </p>
            <div className={`grid grid-cols-2 gap-2 ${type === 'voice' || type === 'music' ? 'grid-cols-1' : ''}`}>
              {type === 'voice' || type === 'music' ? (
                suggestedAssets.map((asset) => (
                  <div
                    key={asset.id + '-suggested'}
                    onClick={() => handleAssetClick(asset)}
                    className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/30 cursor-pointer hover:border-amber-400 flex items-center gap-2"
                  >
                    <Icon className={`w-4 h-4 ${type === 'music' ? 'text-amber-400' : 'text-indigo-400'}`} />
                    <span className="text-xs text-zinc-300 truncate">{asset.name}</span>
                  </div>
                ))
              ) : (
                suggestedAssets.map((asset) => (
                  <div
                    key={asset.id + '-suggested'}
                    onClick={() => handleAssetClick(asset)}
                    className={`${aspectRatio === 'video' ? 'aspect-video' : 'aspect-square'} rounded-lg overflow-hidden border-2 cursor-pointer relative ${
                      type === 'background' && selectedBackground === asset.id
                        ? "border-indigo-500 ring-2 ring-indigo-500/30"
                        : "border-amber-500/50 hover:border-amber-400"
                    }`}
                  >
                    <img src={getAssetThumbnail(asset)} alt={asset.name} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 pt-3">
                      <span className="text-[10px] text-white truncate block font-medium">{asset.name}</span>
                    </div>
                    <div className="absolute top-1 right-1 bg-amber-500 rounded px-1 py-0.5">
                      <Zap className="w-2.5 h-2.5 text-black" />
                    </div>
                  </div>
                ))
              )}
            </div>
            {otherAssets.length > 0 && (
              <div className="h-px bg-zinc-700 my-3" />
            )}
          </div>
        )}
        
        {/* All Assets Section */}
        <div className={`grid grid-cols-2 gap-2 ${type === 'voice' || type === 'music' ? 'grid-cols-1' : ''}`}>
          {uploadingType === type && (
            <div className={`${aspectRatio === 'video' ? 'aspect-video' : 'aspect-square'} rounded-lg bg-zinc-800 border border-dashed border-indigo-500 flex items-center justify-center`}>
              <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
            </div>
          )}
          {type === 'voice' || type === 'music' ? (
            visibleOtherAssets.map((asset) => (
              <div
                key={asset.id}
                onClick={() => handleAssetClick(asset)}
                className="p-2 bg-zinc-800/50 rounded-lg border border-zinc-700 cursor-pointer hover:border-indigo-500 flex items-center gap-2"
              >
                <Icon className={`w-4 h-4 ${type === 'music' ? 'text-amber-400' : 'text-indigo-400'}`} />
                <span className="text-xs text-zinc-300 truncate">{asset.name}</span>
              </div>
            ))
          ) : (
            visibleOtherAssets.map((asset) => (
              <div
                key={asset.id}
                onClick={() => handleAssetClick(asset)}
                className={`${aspectRatio === 'video' ? 'aspect-video' : 'aspect-square'} rounded-lg overflow-hidden border-2 cursor-pointer relative ${
                  type === 'background' && selectedBackground === asset.id
                    ? "border-indigo-500 ring-2 ring-indigo-500/30"
                    : "border-zinc-700 hover:border-indigo-500"
                }`}
              >
                <img src={getAssetThumbnail(asset)} alt={asset.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1 pt-3">
                  <span className="text-[10px] text-white truncate block font-medium">{asset.name}</span>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Load More / Show Less Buttons */}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleAssetExpanded(type)}
            className="w-full mt-2 text-xs text-zinc-400 hover:text-white h-7"
          >
            <ChevronDown className="w-3 h-3 mr-1" />
            Show {remainingCount} more
          </Button>
        )}
        {isExpanded && otherAssets.length > INITIAL_ASSET_LIMIT && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleAssetExpanded(type)}
            className="w-full mt-2 text-xs text-zinc-400 hover:text-white h-7"
          >
            <ChevronUp className="w-3 h-3 mr-1" />
            Show less
          </Button>
        )}
        
        {/* Empty State */}
        {allAssets.length === 0 && !uploadingType && (
          <div className="flex flex-col items-center justify-center py-8 text-zinc-500 text-center">
            <Icon className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">No {label.toLowerCase()} yet</p>
            <p className="text-[10px] mt-1">Upload or import to add</p>
          </div>
        )}
          </>
        )}
      </ScrollArea>
    </div>
  );
};

export default function AnimationStudio() {
  const { projectId, sceneId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  // Use custom hooks for better state management
  // These hooks are imported but we'll keep existing state for now to avoid breaking changes
  // TODO: Gradually migrate state to use these hooks
  const assetHook = useAssets(projectId, getAuthHeaders);
  const lipsyncHook = useLipsync(sceneId);
  
  const [scene, setScene] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [directorPrompt, setDirectorPrompt] = useState("");
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [placedCharacters, setPlacedCharacters] = useState([]);
  const [audioTracks, setAudioTracks] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [voiceMapping, setVoiceMapping] = useState({});
  const [actionLevel, setActionLevel] = useState(50);
  const [playheadPosition, setPlayheadPosition] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [resizing, setResizing] = useState(null);
  const [draggingCharacter, setDraggingCharacter] = useState(null);
  const [scriptSegment, setScriptSegment] = useState("");
  const [isEditingScript, setIsEditingScript] = useState(false);
  
  // Prompt builder fields
  const [promptRules, setPromptRules] = useState("Static background, only the character moves. Animate the SAME character from the reference image. This is one continuous shot of the exact same person. Do NOT change the face, eyes, nose, mouth, jaw, hair, or identity. The character must look identical to the reference image in every frame. Subtle head movement, natural blinking, subtle mouth moving to speech. Use face-tracking and identity preservation. Treat the reference image as a fixed character model. Do not reinterpret or redraw the face.");
  const [promptStyle, setPromptStyle] = useState("A 2D animated scene in the style of the Archer series on FX.");
  const [promptSetting, setPromptSetting] = useState("");
  const [promptMovement, setPromptMovement] = useState("");
  const [promptAvoid, setPromptAvoid] = useState("different face, new person, face morphing, facial drift, inconsistent identity, regenerated face, zoom, pan, blur, distort, music, low quality, moving background, animated background, camera movement, parallax, background motion");
  const [expandedPromptSections, setExpandedPromptSections] = useState({}); // Track which sections are expanded
  
  // Helper to build combined prompt for display
  const buildCombinedPrompt = () => {
    const parts = [];
    if (promptRules.trim()) parts.push(`RULES: ${promptRules.trim()}`);
    if (promptStyle.trim()) parts.push(`STYLE: ${promptStyle.trim()}`);
    if (promptSetting.trim()) parts.push(`SETTING: ${promptSetting.trim()}`);
    if (promptMovement.trim()) parts.push(`MOVEMENT: ${promptMovement.trim()}`);
    return parts.join("\n\n");
  };
  
  const [previewMode, setPreviewMode] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [selectedVoiceForMerge, setSelectedVoiceForMerge] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergedVideoUrl, setMergedVideoUrl] = useState(null);
  const [assetSearch, setAssetSearch] = useState("");
  const [costEstimate, setCostEstimate] = useState(null);
  const [applyingLipSync, setApplyingLipSync] = useState(false);
  const [lipSyncVideoUrl, setLipSyncVideoUrl] = useState(null);
  const [showMouthPositionModal, setShowMouthPositionModal] = useState(false);
  const [sceneMouthPosition, setSceneMouthPosition] = useState(null);
  
  // Character Profiles state
  const [characterProfiles, setCharacterProfiles] = useState([]);
  const [selectedCharacterProfile, setSelectedCharacterProfile] = useState(null);
  
  // Smart Import state for characters
  const [showSmartImport, setShowSmartImport] = useState(false);
  const [smartImportFiles, setSmartImportFiles] = useState([]);
  
  // New state for enhanced features
  const [backgroundZoom, setBackgroundZoom] = useState(100);
  const [backgroundPan, setBackgroundPan] = useState({ x: 0, y: 0 });
  const [backgroundBlur, setBackgroundBlur] = useState(0);
  const [isPanningBackground, setIsPanningBackground] = useState(false);
  const [showLayersModal, setShowLayersModal] = useState(false);
  const [editingLayerName, setEditingLayerName] = useState(null);
  const [draggingProperty, setDraggingProperty] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [assetLibraryCollapsed, setAssetLibraryCollapsed] = useState(false);
  
  // Import from project state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importAssetType, setImportAssetType] = useState(null);
  const [importableProjects, setImportableProjects] = useState([]);
  const [selectedImportProject, setSelectedImportProject] = useState("");
  const [importableAssets, setImportableAssets] = useState([]);
  const [selectedImportAssets, setSelectedImportAssets] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Asset library load more state - tracks which asset types show all items
  const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
  const [deletingAllAssets, setDeletingAllAssets] = useState(false);
  const INITIAL_ASSET_LIMIT = 7;
  
  // Scene navigation state
  const [projectScenes, setProjectScenes] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedState, setLastSavedState] = useState(null);
  
  // Pipeline workflow state
  const [currentStage, setCurrentStage] = useState("strategy");
  const [animationTakes, setAnimationTakes] = useState([]);
  const [lipsyncTakes, setLipsyncTakes] = useState([]);
  const [activeAnimationIndex, setActiveAnimationIndex] = useState(0);
  const [activeLipsyncIndex, setActiveLipsyncIndex] = useState(0);
  const [activeTakeIndex, setActiveTakeIndex] = useState(0); // Unified take index for Print step
  const [printedTakeId, setPrintedTakeId] = useState(null); // ID of the take marked for final output
  const [fullKlingPrompt, setFullKlingPrompt] = useState(""); // Full editable prompt for Kling
  
  // Strategy workflow state (for uploading completed videos)
  const [strategyChoice, setStrategyChoice] = useState(null); // "compose" or "upload"
  const [uploadedSceneVideo, setUploadedSceneVideo] = useState(null); // URL of uploaded completed video
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [lipsyncUnlockedForUpload, setLipsyncUnlockedForUpload] = useState(false); // Track if user has unlocked lip sync for uploaded video
  
  // O1 Integration state
  const [o1Elements, setO1Elements] = useState([]); // O1 character elements with reference images (max 4)
  const [styleImages, setStyleImages] = useState([]); // Additional style/mood reference images (max 3)
  const [videoReference, setVideoReference] = useState(null); // Video reference (@Video1)
  // useStartFrame removed - always true now (start frame is always used)
  const [o1Duration, setO1Duration] = useState("5"); // Video duration: "5" or "10"
  const [o1AspectRatio, setO1AspectRatio] = useState("16:9"); // Aspect ratio
  const [actionDirection, setActionDirection] = useState(""); // User's plain language action description
  const [improvedPrompt, setImprovedPrompt] = useState(""); // AI-optimized prompt
  const [o1NegativePrompt, setO1NegativePrompt] = useState("blur, distortion, morphing, low quality, face change, identity drift");

  // Scene type (action vs dialogue) for Preview step
  const [sceneType, setSceneType] = useState("action"); // "action" or "dialogue"
  const [dialoguePrompt, setDialoguePrompt] = useState(""); // Prompt for Hedra dialogue generation
  const [selectedVoiceForDialogue, setSelectedVoiceForDialogue] = useState(null); // Voice file ID for dialogue mode
  const [generatingDialogue, setGeneratingDialogue] = useState(false); // Tracking dialogue generation

  // Default dialogue prompt
  const DEFAULT_DIALOGUE_PROMPT = "2D cartoon character speaking with subtle lip movements. Minimal head motion, gentle eye blinks. Static body, only mouth animates to speech. Flat animation style, no 3D depth or morphing. Clean simple movements like traditional hand-drawn animation.";

  // Preview Scene loading state
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [pendingPreviewNavigation, setPendingPreviewNavigation] = useState(false);

  // Effect to navigate to preview after scene state is updated with composite_image_url
  useEffect(() => {
    if (pendingPreviewNavigation && scene?.composite_image_url) {
      setPendingPreviewNavigation(false);
      setCurrentStage("preview");
    }
  }, [pendingPreviewNavigation, scene?.composite_image_url]);

  // Skip Lip Sync setting (saved per scene)
  const [skipLipsync, setSkipLipsync] = useState(false);

  // Legacy end frame state (kept for backward compatibility, removed from UI)
  const [hasEndFrame, setHasEndFrame] = useState(false);
  const [endFrameCharacters, setEndFrameCharacters] = useState([]);
  const [editingEndFrame, setEditingEndFrame] = useState(false);
  const [endFramePreviewUrl, setEndFramePreviewUrl] = useState(null);
  
  // Lip Sync timeline state
  const [audioOffset, setAudioOffset] = useState(0); // Offset in seconds for audio sync
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [audioWaveform, setAudioWaveform] = useState([]);
  const [trimStart, setTrimStart] = useState(0); // Trim start point in seconds
  const [trimEnd, setTrimEnd] = useState(null); // Trim end point in seconds (null = full length)
  const [isDraggingTrim, setIsDraggingTrim] = useState(null); // 'start', 'end', or null
  
  // Server-side preview sync state
  const [syncPreviewUrl, setSyncPreviewUrl] = useState(null); // URL of the merged preview video
  const [generatingSyncPreview, setGeneratingSyncPreview] = useState(false); // Loading state for preview generation
  
  // Grid overlay state for compose stage
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(3); // Default 3x3 grid
  const [gridLineWidth, setGridLineWidth] = useState(1); // Grid line thickness (1-5px)
  
  // Compose stage right panel tab
  const [composeRightTab, setComposeRightTab] = useState("scene"); // "scene" or "layers"
  
  // Copy from another shot dialog
  const [showCopyFromShotDialog, setShowCopyFromShotDialog] = useState(false);
  const [copySourceSearch, setCopySourceSearch] = useState("");
  const [copyingFromShot, setCopyingFromShot] = useState(false);
  
  // Duplicate shot dialog
  const [showDuplicateShotDialog, setShowDuplicateShotDialog] = useState(false);
  const [duplicateShotName, setDuplicateShotName] = useState("");
  const [duplicateInsertIndex, setDuplicateInsertIndex] = useState(null);
  const [duplicatingShot, setDuplicatingShot] = useState(false);
  
  // Performance optimization: Visual offset during interactions (CSS transform approach)
  // These store temporary visual offsets during drag/resize without triggering React re-renders
  const [visualDragOffset, setVisualDragOffset] = useState({ x: 0, y: 0 }); // Offset during character drag
  const [visualResizeScale, setVisualResizeScale] = useState(null); // Scale during resize (null = use state)
  const [isInteracting, setIsInteracting] = useState(false); // True during any drag/resize operation
  const pendingSaveRef = useRef(false); // Track if save is pending after interaction
  
  // Voice import from other project
  const [showVoiceImportDialog, setShowVoiceImportDialog] = useState(false);
  const [voiceImportProjects, setVoiceImportProjects] = useState([]);
  const [selectedVoiceImportProject, setSelectedVoiceImportProject] = useState(null);
  const [voiceImportAssets, setVoiceImportAssets] = useState([]);
  const [loadingVoiceImport, setLoadingVoiceImport] = useState(false);
  
  // Elapsed timer state for generation tracking
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedTimerRef = useRef(null);
  
  const videoPreviewRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const playheadIntervalRef = useRef(null);
  const audioDelayTimeoutRef = useRef(null);
  
  const canvasRef = useRef(null);
  const resizeStartRef = useRef(null);
  const charDragStartRef = useRef(null);
  const propertyDragStartRef = useRef(null);
  const panStartRef = useRef(null);
  const autosaveTimeoutRef = useRef(null);

  // Fetch all scenes for navigation
  const fetchProjectScenes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/scenes`, { headers: getAuthHeaders() });
      setProjectScenes(response.data || []);
    } catch (error) {
      console.error("Failed to load project scenes");
    }
  };

  // Get current scene index and navigation info
  const currentSceneIndex = projectScenes.findIndex(s => s.id === sceneId);
  const hasPreviousScene = currentSceneIndex > 0;
  const hasNextScene = currentSceneIndex < projectScenes.length - 1 && currentSceneIndex !== -1;
  
  const navigateToScene = (direction) => {
    const newIndex = direction === 'prev' ? currentSceneIndex - 1 : currentSceneIndex + 1;
    if (newIndex >= 0 && newIndex < projectScenes.length) {
      const newSceneId = projectScenes[newIndex].id;
      navigate(`/project/${projectId}/studio/${newSceneId}`);
    }
  };

  useEffect(() => {
    fetchScene();
    fetchAssets();
    fetchCharacterProfiles();
    fetchCostEstimate();
    fetchProjectScenes();
  }, [sceneId, projectId]);

  // Elapsed timer effect - track time during generation or lip sync
  useEffect(() => {
    if (generating || applyingLipSync) {
      // Start timer
      setGenerationStartTime(Date.now());
      setElapsedTime(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 10); // Update every 10ms for milliseconds
      }, 10);
    } else {
      // Stop timer
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
      setGenerationStartTime(null);
    }
    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
      }
    };
  }, [generating, applyingLipSync]);

  // Format elapsed time as mm:ss:ms
  const formatElapsedTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const milliseconds = Math.floor((ms % 1000) / 10);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${milliseconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    let interval;
    if (scene?.status === "generating") {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/scenes/${sceneId}/status`, { headers: getAuthHeaders() });
          if (response.data.status !== "generating") {
            setScene(prev => ({
              ...prev,
              status: response.data.status,
              generated_video_url: response.data.video_url
            }));
            setGenerating(false);
            if (response.data.status === "completed") {
              // Refresh takes data after generation completes
              const updatedScene = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
              setAnimationTakes(updatedScene.data.animation_takes || []);
              setActiveAnimationIndex(updatedScene.data.active_animation_index || 0);
              toast.success("Animation generated successfully!");
              // Stay in animate stage - don't show modal, video is in canvas
            } else if (response.data.status === "failed") {
              toast.error("Animation generation failed");
            }
          }
        } catch (error) {
          console.error("Status check failed");
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [scene?.status, sceneId, currentStage]);

  const fetchScene = async () => {
    try {
      const response = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
      setScene(response.data);
      setSelectedBackground(response.data.background_id);
      const characters = (response.data.characters || []).map(c => ({
        ...c,
        opacity: c.opacity ?? 100,
        rotation: c.rotation ?? 0,
        blur: c.blur ?? 0
      }));
      setPlacedCharacters(characters);
      setAudioTracks(response.data.audio_tracks || []);
      setVoiceMapping(response.data.voice_mapping || {});
      setActionLevel(response.data.action_level || 50);
      setDirectorPrompt(response.data.director_prompt || "");
      setScriptSegment(response.data.script_segment || "");
      // Load action direction from scene data
      setActionDirection(response.data.action_direction || "");
      const bgZoom = response.data.background_zoom ?? 100;
      const bgPan = response.data.background_pan ?? { x: 0, y: 0 };
      const bgBlur = response.data.background_blur ?? 0;
      setBackgroundZoom(bgZoom);
      setBackgroundPan(bgPan);
      setBackgroundBlur(bgBlur);
      if (response.data.mouth_position) setSceneMouthPosition(response.data.mouth_position);
      
      // Load prompt builder fields with defaults
      if (response.data.prompt_rules) setPromptRules(response.data.prompt_rules);
      if (response.data.prompt_style) setPromptStyle(response.data.prompt_style);
      if (response.data.prompt_setting) setPromptSetting(response.data.prompt_setting);
      if (response.data.prompt_movement) setPromptMovement(response.data.prompt_movement);
      if (response.data.prompt_avoid) setPromptAvoid(response.data.prompt_avoid);
      
      // Load skip lipsync setting
      if (response.data.skip_lipsync !== undefined) setSkipLipsync(response.data.skip_lipsync);
      
      // Load takes data - handle migration from legacy format
      let animTakes = response.data.animation_takes || [];
      let lipTakes = response.data.lipsync_takes || [];
      
      // Migrate legacy lipsync_video_url to takes if not already migrated
      if (response.data.lipsync_video_url && lipTakes.length === 0) {
        lipTakes = [{
          id: 'legacy-lipsync',
          video_url: response.data.lipsync_video_url,
          voice_asset_id: '',
          voice_name: 'Legacy',
          created_at: response.data.created_at || new Date().toISOString(),
          status: 'completed'
        }];
      }
      
      setAnimationTakes(animTakes);
      setLipsyncTakes(lipTakes);
      setActiveAnimationIndex(response.data.active_animation_index || 0);
      setActiveLipsyncIndex(response.data.active_lipsync_index || 0);
      setActiveTakeIndex(response.data.active_take_index || 0);
      setPrintedTakeId(response.data.printed_take_id || null);
      
      // Load end frame data
      if (response.data.has_end_frame) {
        setHasEndFrame(true);
        setEndFrameCharacters(response.data.end_frame_characters || []);
        setEndFramePreviewUrl(response.data.end_frame_url || null);
      }
      
      // Also update lipSyncVideoUrl state from lipsync_takes if available
      if (lipTakes.length > 0) {
        const activeIdx = response.data.active_lipsync_index || 0;
        setLipSyncVideoUrl(lipTakes[activeIdx]?.video_url || null);
      }
      
      // Load O1 Preview state (if saved)
      // Filter saved elements to only include those matching current characters
      if (response.data.o1_elements && response.data.o1_elements.length > 0) {
        const savedElements = response.data.o1_elements;
        const currentCharNames = characters.map(c => 
          (c.character_name || c.name || '').toLowerCase()
        );
        const validElements = savedElements.filter(elem => {
          const elemName = (elem.name || '').toLowerCase();
          return currentCharNames.includes(elemName);
        });
        console.log(`Loaded ${validElements.length} valid elements of ${savedElements.length} saved (current chars: ${currentCharNames.join(', ')})`);
        setO1Elements(validElements);
      }
      if (response.data.o1_style_images && response.data.o1_style_images.length > 0) {
        setStyleImages(response.data.o1_style_images);
      }
      if (response.data.o1_improved_prompt) {
        setImprovedPrompt(response.data.o1_improved_prompt);
      }
      if (response.data.o1_negative_prompt) {
        setO1NegativePrompt(response.data.o1_negative_prompt);
      }
      
      // Load Lip-sync state (if saved)
      if (response.data.lipsync_selected_voice_id) {
        setSelectedVoiceForMerge(response.data.lipsync_selected_voice_id);
      }
      if (response.data.lipsync_audio_offset !== undefined && response.data.lipsync_audio_offset !== null) {
        setAudioOffset(response.data.lipsync_audio_offset);
      }
      if (response.data.lipsync_trim_start !== undefined && response.data.lipsync_trim_start !== null) {
        setTrimStart(response.data.lipsync_trim_start);
      }
      if (response.data.lipsync_trim_end !== undefined && response.data.lipsync_trim_end !== null) {
        setTrimEnd(response.data.lipsync_trim_end);
      }
      
      // Set initial saved state for autosave tracking
      setLastSavedState(JSON.stringify({
        background_id: response.data.background_id,
        characters: characters,
        audio_tracks: response.data.audio_tracks || [],
        voice_mapping: response.data.voice_mapping || {},
        action_level: response.data.action_level || 50,
        director_prompt: response.data.director_prompt || "",
        script_segment: response.data.script_segment || "",
        background_zoom: bgZoom,
        background_pan: bgPan,
        background_blur: bgBlur,
        prompt_rules: response.data.prompt_rules || promptRules,
        prompt_style: response.data.prompt_style || promptStyle,
        prompt_setting: response.data.prompt_setting || "",
        prompt_movement: response.data.prompt_movement || "",
        prompt_avoid: response.data.prompt_avoid || promptAvoid
      }));
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error("Failed to load scene");
      navigate(`/project/${projectId}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/assets`, { headers: getAuthHeaders() });
      setAssets(response.data);
    } catch (error) {
      console.error("Failed to load assets");
    }
  };

  const fetchCharacterProfiles = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/characters`, { headers: getAuthHeaders() });
      // Sort alphabetically
      const sorted = (response.data || []).sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      setCharacterProfiles(sorted);
    } catch (error) {
      console.error("Failed to load character profiles");
    }
  };

  const fetchCostEstimate = async () => {
    try {
      const response = await axios.get(`${API}/scenes/${sceneId}/cost-estimate`, { headers: getAuthHeaders() });
      setCostEstimate(response.data);
    } catch (error) {
      console.error("Failed to load cost estimate");
    }
  };

  // Auto-save reference images (o1Elements, styleImages) when they change
  // This ensures reference images persist when user navigates away or refreshes
  useEffect(() => {
    // Skip if scene not loaded yet or if we're still loading
    if (!sceneId || loading || !scene) return;
    
    // Debounce the save to avoid too many API calls
    const saveTimer = setTimeout(async () => {
      try {
        await axios.put(`${API}/scenes/${sceneId}`, {
          o1_elements: o1Elements,
          o1_style_images: styleImages
        }, { headers: getAuthHeaders() });
        console.log('[AutoSave] Reference images saved');
      } catch (error) {
        console.error('[AutoSave] Failed to save reference images:', error);
      }
    }, 1000); // Wait 1 second after last change before saving
    
    return () => clearTimeout(saveTimer);
  }, [o1Elements, styleImages, sceneId, loading, scene]);

  // Import functions
  const handleOpenImport = async (assetType) => {
    setImportAssetType(assetType);
    setSelectedImportProject("");
    setImportableAssets([]);
    setSelectedImportAssets([]);
    setShowImportDialog(true);
    
    setLoadingProjects(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/importable-projects`, { headers: getAuthHeaders() });
      setImportableProjects(response.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectSelect = async (sourceProjectId) => {
    setSelectedImportProject(sourceProjectId);
    setSelectedImportAssets([]);
    if (!sourceProjectId) return;
    
    setLoadingAssets(true);
    try {
      const response = await axios.get(`${API}/projects/${sourceProjectId}/exportable-assets`, { headers: getAuthHeaders() });
      const filtered = response.data.filter(a => a.type === importAssetType);
      setImportableAssets(filtered);
    } catch (error) {
      toast.error("Failed to load assets");
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleImportAssets = async () => {
    if (selectedImportAssets.length === 0) {
      toast.error("Please select at least one asset");
      return;
    }
    setImporting(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/import-assets`, {
        source_project_id: selectedImportProject,
        asset_ids: selectedImportAssets
      }, { headers: getAuthHeaders() });
      toast.success(`Imported ${response.data.imported_count} asset(s)`);
      setShowImportDialog(false);
      // Refresh both assets and character profiles
      fetchAssets();
      fetchCharacterProfiles();
    } catch (error) {
      toast.error("Failed to import assets");
    } finally {
      setImporting(false);
    }
  };

  // Smart Import handler for characters
  const handleSmartImportConfirm = async (importData) => {
    let created = 0;
    let updated = 0;
    let imagesAdded = 0;
    
    for (const group of importData) {
      try {
        let characterId = group.existingCharacterId;
        
        // Create new character if doesn't exist
        if (!characterId) {
          const createResponse = await axios.post(`${API}/projects/${projectId}/characters`, {
            name: group.name,
            description: ""
          }, { headers: getAuthHeaders() });
          characterId = createResponse.data.id;
          created++;
        } else {
          updated++;
        }
        
        // Upload each file and add to character using direct S3 upload
        for (const file of group.files) {
          await uploadFileToS3(file, projectId, {
            type: 'character',
            name: file.name,
            characterProfileId: characterId
          });
          
          imagesAdded++;
        }
      } catch (error) {
        console.error(`Failed to import ${group.name}:`, error);
        toast.error(`Failed to import ${group.name}`);
      }
    }
    
    // Refresh data
    await fetchAssets();
    await fetchCharacterProfiles();
    
    const messages = [];
    if (created > 0) messages.push(`${created} new character${created !== 1 ? 's' : ''}`);
    if (updated > 0) messages.push(`${updated} updated`);
    messages.push(`${imagesAdded} image${imagesAdded !== 1 ? 's' : ''} added`);
    
    toast.success(`Import complete: ${messages.join(', ')}`);
    
    setShowSmartImport(false);
    setSmartImportFiles([]);
  };

  const handleUploadAsset = async (file, assetType) => {
    if (!file) return;
    setUploadingType(assetType);
    try {
      await uploadFileToS3(file, projectId, {
        type: assetType,
        name: file.name.replace(/\.[^/.]+$/, "")
      });
      toast.success(`${assetType} uploaded successfully!`);
      fetchAssets();
    } catch (error) {
      console.error("Upload error:", error.response?.data || error);
      toast.error(`Failed to upload ${assetType}`);
    } finally {
      setUploadingType(null);
    }
  };

  const handleDeleteAllAssets = async () => {
    setDeletingAllAssets(true);
    try {
      const response = await axios.delete(`${API}/projects/${projectId}/assets`, { headers: getAuthHeaders() });
      toast.success(`Deleted ${response.data.deleted_count} assets`);
      setAssets([]);
      // Clear any placed characters that referenced deleted assets
      setPlacedCharacters([]);
      setSelectedBackground(null);
      setAudioTracks([]);
    } catch (error) {
      console.error("Delete all assets error:", error);
      toast.error("Failed to delete assets");
    } finally {
      setDeletingAllAssets(false);
    }
  };

  const handleSaveScene = async (silent = false) => {
    try {
      const sceneData = {
        background_id: selectedBackground,
        characters: placedCharacters,
        audio_tracks: audioTracks,
        voice_mapping: voiceMapping,
        action_level: actionLevel,
        director_prompt: directorPrompt,
        script_segment: scriptSegment,
        action_direction: actionDirection,
        background_zoom: backgroundZoom,
        background_pan: backgroundPan,
        background_blur: backgroundBlur,
        prompt_rules: promptRules,
        prompt_style: promptStyle,
        prompt_setting: promptSetting,
        prompt_movement: promptMovement,
        prompt_avoid: promptAvoid,
        skip_lipsync: skipLipsync,
        active_take_index: activeTakeIndex
      };
      await axios.put(`${API}/scenes/${sceneId}`, sceneData, { headers: getAuthHeaders() });
      setLastSavedState(JSON.stringify(sceneData));
      setHasUnsavedChanges(false);
      if (!silent) {
        toast.success("Shot saved");
      }
    } catch (error) {
      if (!silent) {
        toast.error("Failed to save scene");
      }
    }
  };

  // State for composite generation during save
  const [savingWithComposite, setSavingWithComposite] = useState(false);

  // Save with composite generation - used by explicit Save button click on Compose step
  const handleSaveWithComposite = async () => {
    // First do a regular save
    await handleSaveScene(true);
    
    // Only generate composite on Compose step with at least one asset
    if (currentStage !== 'compose' || (!selectedBackground && placedCharacters.length === 0)) {
      toast.success("Shot saved");
      return;
    }
    
    setSavingWithComposite(true);
    try {
      const canvasWidth = canvasRef.current?.offsetWidth || 1920;
      const canvasHeight = canvasRef.current?.offsetHeight || 1080;
      
      // Start preview generation (creates composite image)
      const previewResponse = await axios.post(`${API}/preview`, {
        scene_id: sceneId,
        canvas_width: canvasWidth,
        canvas_height: canvasHeight
      }, { headers: getAuthHeaders() });
      
      // Check if preview completed synchronously (new behavior) or needs polling (legacy)
      if (previewResponse.data.status === 'completed' && previewResponse.data.composite_url) {
        // Preview completed synchronously - no polling needed
        setScene(prev => ({ ...prev, composite_image_url: previewResponse.data.composite_url }));
        toast.success("Shot saved with preview image");
      } else if (previewResponse.data.job_id) {
        // Legacy: Poll for completion
        const result = await pollPreviewJobCompletion(previewResponse.data.job_id, null);
        if (result.success && result.composite_url) {
          // Update scene state with composite URL
          setScene(prev => ({ ...prev, composite_image_url: result.composite_url }));
          toast.success("Shot saved with preview image");
        } else {
          // Still saved, just composite failed
          toast.success("Shot saved (preview image generation failed)");
        }
      } else {
        toast.success("Shot saved");
      }
    } catch (error) {
      console.error('Composite generation failed:', error);
      toast.success("Shot saved (preview image generation failed)");
    } finally {
      setSavingWithComposite(false);
    }
  };

  // Handle Skip Lip Sync - saves setting and navigates to print
  const handleSkipLipsync = async () => {
    setSkipLipsync(true);
    try {
      await axios.put(`${API}/scenes/${sceneId}`, { skip_lipsync: true }, { headers: getAuthHeaders() });
      toast.success("Skipping lip sync for this shot");
      setCurrentStage("print");
    } catch (error) {
      toast.error("Failed to save setting");
    }
  };

  // Poll for preview job completion and return the composite_image_url
  // Uses AbortController to allow cancellation
  const pollPreviewJobCompletion = async (jobId, abortSignal, maxAttempts = 30, intervalMs = 1000) => {
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Check if cancelled
      if (abortSignal?.aborted) {
        return { success: false, error: 'Cancelled', cancelled: true };
      }
      
      try {
        const statusResponse = await axios.get(`${API}/preview/${jobId}/status`, {
          headers: getAuthHeaders(),
          signal: abortSignal
        });
        const { status, composite_url, error } = statusResponse.data;
        
        consecutiveErrors = 0;
        
        if (status === 'completed') {
          return { success: true, composite_url };
        } else if (status === 'failed') {
          return { success: false, error: error || 'Preview generation failed' };
        }
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      } catch (error) {
        if (error.name === 'CanceledError' || abortSignal?.aborted) {
          return { success: false, error: 'Cancelled', cancelled: true };
        }
        consecutiveErrors++;
        console.error(`Error polling preview status (attempt ${attempt + 1}, consecutive errors: ${consecutiveErrors}):`, error);
        
        if (consecutiveErrors >= maxConsecutiveErrors) {
          return { success: false, error: 'Failed to check preview status after multiple retries' };
        }
        
        await new Promise(resolve => setTimeout(resolve, intervalMs * 2));
      }
    }
    return { success: false, error: 'Preview generation timed out' };
  };

  // Auto-capture composite - DISABLED to prevent request storms
  // The composite is captured when user clicks "Preview Shot" button instead
  // This prevents multiple parallel polling loops from overwhelming the backend
  const compositeDebounceRef = useRef(null);
  const autoCompositeAbortRef = useRef(null);
  
  // Clean up abort controller on unmount
  useEffect(() => {
    return () => {
      if (autoCompositeAbortRef.current) {
        autoCompositeAbortRef.current.abort();
      }
      if (compositeDebounceRef.current) {
        clearTimeout(compositeDebounceRef.current);
      }
    };
  }, []);

  // Track changes for autosave
  const getCurrentState = useCallback(() => {
    return JSON.stringify({
      background_id: selectedBackground,
      characters: placedCharacters,
      audio_tracks: audioTracks,
      voice_mapping: voiceMapping,
      action_level: actionLevel,
      director_prompt: directorPrompt,
      script_segment: scriptSegment,
      background_zoom: backgroundZoom,
      background_pan: backgroundPan,
      background_blur: backgroundBlur,
      prompt_rules: promptRules,
      prompt_style: promptStyle,
      prompt_setting: promptSetting,
      prompt_movement: promptMovement,
      prompt_avoid: promptAvoid
    });
  }, [selectedBackground, placedCharacters, audioTracks, voiceMapping, actionLevel, directorPrompt, scriptSegment, backgroundZoom, backgroundPan, backgroundBlur, promptRules, promptStyle, promptSetting, promptMovement, promptAvoid]);

  // Autosave effect - saves 2 seconds after last change
  useEffect(() => {
    if (!scene) return;
    
    const currentState = getCurrentState();
    if (lastSavedState && currentState !== lastSavedState) {
      setHasUnsavedChanges(true);
      
      // Clear existing timeout
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
      
      // Set new autosave timeout (2 seconds after last change)
      autosaveTimeoutRef.current = setTimeout(() => {
        handleSaveScene(true); // Silent save
      }, 2000);
    }
    
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [getCurrentState, lastSavedState, scene]);

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        // Trigger a synchronous save attempt
        const sceneData = {
          background_id: selectedBackground,
          characters: placedCharacters,
          audio_tracks: audioTracks,
          voice_mapping: voiceMapping,
          action_level: actionLevel,
          director_prompt: directorPrompt,
          script_segment: scriptSegment,
          background_zoom: backgroundZoom,
          background_pan: backgroundPan,
          background_blur: backgroundBlur
        };
        
        // Use sendBeacon for reliable saving on page unload
        const blob = new Blob([JSON.stringify(sceneData)], { type: 'application/json' });
        navigator.sendBeacon(`${API}/scenes/${sceneId}`, blob);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, selectedBackground, placedCharacters, audioTracks, voiceMapping, actionLevel, directorPrompt, scriptSegment, backgroundZoom, backgroundPan, backgroundBlur, sceneId]);

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      await handleSaveScene(true); // Silent save first
      
      if (!canvasRef.current) {
        toast.error("Canvas not found");
        return;
      }
      
      // Capture the canvas element exactly as it appears
      toast.info("Capturing canvas...");
      const canvas = await html2canvas(canvasRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#000000',
        scale: 2, // Higher resolution capture
        logging: false,
        imageTimeout: 15000
      });
      
      // Convert to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
      
      // Create form data with the captured image
      const formData = new FormData();
      formData.append('scene_id', sceneId);
      formData.append('canvas_image', blob, 'canvas_capture.png');
      
      // Send to backend
      const response = await axios.post(`${API}/preview-from-canvas`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      
      const previewUrl = `${API}/exports/${response.data.filename}?t=${Date.now()}`;
      setPreviewImageUrl(previewUrl);
      setPreviewPrompt(response.data.prompt || "");
      setPreviewMode(true);
      toast.success("Preview generated!");
    } catch (error) {
      console.error("Preview error:", error);
      toast.error("Failed to generate preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleMergeAudio = async () => {
    if (!selectedVoiceForMerge) {
      toast.error("Please select a voice file first");
      return;
    }
    setMerging(true);
    try {
      const response = await axios.post(`${API}/scenes/${sceneId}/merge-audio`, {
        scene_id: sceneId,
        voice_asset_id: selectedVoiceForMerge
      }, { headers: getAuthHeaders() });
      if (response.data.success) {
        const mergedUrl = `${API}/exports/${response.data.filename}`;
        setMergedVideoUrl(mergedUrl);
        toast.success("Audio merged successfully!");
      }
    } catch (error) {
      toast.error("Failed to merge audio");
    } finally {
      setMerging(false);
    }
  };

  const handleApplyLipSync = async () => {
    if (!selectedVoiceForMerge) {
      toast.error("Please select a voice file first");
      return;
    }
    setApplyingLipSync(true);
    try {
      // Save Lip-sync state before applying
      await axios.put(`${API}/scenes/${sceneId}`, {
        lipsync_selected_voice_id: selectedVoiceForMerge,
        lipsync_audio_offset: audioOffset,
        lipsync_trim_start: trimStart,
        lipsync_trim_end: trimEnd
      }, { headers: getAuthHeaders() });
      
      toast.info("Submitting to Sync Labs for lip sync...");
      
      // Calculate effective trim points
      const effectiveTrimEnd = trimEnd !== null ? trimEnd : videoDuration;
      const hasTrim = trimStart > 0 || (trimEnd !== null && trimEnd < videoDuration);
      
      // Build request - include video_url if using uploaded video
      const requestData = {
        voice_asset_id: selectedVoiceForMerge,
        audio_offset: audioOffset,
        trim_start: hasTrim ? trimStart : null,
        trim_end: hasTrim ? effectiveTrimEnd : null
      };
      
      // If we have an uploaded video, pass it directly
      if (uploadedSceneVideo) {
        requestData.video_url = uploadedSceneVideo;
        requestData.is_uploaded_video = true;
      }
      
      const response = await axios.post(`${API}/scenes/${sceneId}/apply-lipsync`, requestData, { headers: getAuthHeaders() });
      
      if (response.data.status === "processing" && response.data.job_id) {
        // New async job system - poll for completion
        const jobId = response.data.job_id;
        toast.info("Lip sync processing started. This may take a few minutes...");
        
        // Poll for status
        const pollInterval = 3000; // 3 seconds
        const maxPolls = 200; // 10 minutes max
        let polls = 0;
        
        const pollStatus = async () => {
          try {
            const statusResponse = await axios.get(
              `${API}/scenes/${sceneId}/lipsync-status/${jobId}`,
              { headers: getAuthHeaders() }
            );
            
            const { status, progress, message, result, error } = statusResponse.data;
            
            if (status === "completed" && result) {
              setLipSyncVideoUrl(result.lipsync_video_url);
              // Update takes
              const updatedScene = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
              setLipsyncTakes(updatedScene.data.lipsync_takes || []);
              setActiveLipsyncIndex(updatedScene.data.active_lipsync_index || 0);
              toast.success(`Lip sync complete! Duration: ${result.duration?.toFixed(1)}s`);
              setApplyingLipSync(false);
              // Navigate to export page
              setCurrentStage("print");
              return;
            } else if (status === "failed") {
              toast.error(`Lip sync failed: ${error || "Unknown error"}`);
              setApplyingLipSync(false);
              return;
            }
            
            // Still processing - continue polling
            polls++;
            if (polls < maxPolls) {
              setTimeout(pollStatus, pollInterval);
            } else {
              toast.error("Lip sync timed out. Please try again.");
              setApplyingLipSync(false);
            }
          } catch (pollError) {
            console.error("Error polling lip sync status:", pollError);
            toast.error("Error checking lip sync status");
            setApplyingLipSync(false);
          }
        };
        
        // Start polling
        setTimeout(pollStatus, pollInterval);
        
      } else if (response.data.success) {
        // Legacy sync response (fallback)
        setLipSyncVideoUrl(response.data.lipsync_video_url);
        // Update takes
        const updatedScene = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
        setLipsyncTakes(updatedScene.data.lipsync_takes || []);
        setActiveLipsyncIndex(updatedScene.data.active_lipsync_index || 0);
        toast.success(`Lip sync complete! Duration: ${response.data.duration?.toFixed(1)}s`);
        setApplyingLipSync(false);
        // Navigate to export page
        setCurrentStage("print");
      }
    } catch (error) {
      toast.error("Failed to apply lip sync");
      setApplyingLipSync(false);
    }
  };

  // Takes management functions
  const handleSelectAnimationTake = async (index) => {
    try {
      await axios.put(`${API}/scenes/${sceneId}/animation-takes/active/${index}`, {}, { headers: getAuthHeaders() });
      setActiveAnimationIndex(index);
      // Update scene's generated_video_url to match the selected take
      if (animationTakes[index]) {
        setScene(prev => ({ ...prev, generated_video_url: animationTakes[index].video_url }));
        // Load the prompt that generated this take so user can tweak and regenerate
        if (animationTakes[index].prompt) {
          setFullKlingPrompt(animationTakes[index].prompt);
        }
      }
      toast.success(`Switched to Animation Take ${index + 1}`);
    } catch (error) {
      toast.error("Failed to switch animation take");
    }
  };

  const handleDeleteAnimationTake = async (index) => {
    try {
      const response = await axios.delete(`${API}/scenes/${sceneId}/animation-takes/${index}`, { headers: getAuthHeaders() });
      // Refresh takes
      const updatedScene = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
      setAnimationTakes(updatedScene.data.animation_takes || []);
      setActiveAnimationIndex(updatedScene.data.active_animation_index || 0);
      setScene(prev => ({ ...prev, generated_video_url: updatedScene.data.generated_video_url }));
      toast.success("Animation take deleted");
    } catch (error) {
      toast.error("Failed to delete animation take");
    }
  };

  const handleSelectLipsyncTake = async (index) => {
    try {
      await axios.put(`${API}/scenes/${sceneId}/lipsync-takes/active/${index}`, {}, { headers: getAuthHeaders() });
      setActiveLipsyncIndex(index);
      // Update scene's lipsync_video_url to match the selected take
      if (lipsyncTakes[index]) {
        setLipSyncVideoUrl(lipsyncTakes[index].video_url);
        setScene(prev => ({ ...prev, lipsync_video_url: lipsyncTakes[index].video_url }));
      }
      toast.success(`Switched to Lip Sync Take ${index + 1}`);
    } catch (error) {
      toast.error("Failed to switch lip sync take");
    }
  };

  const handleDeleteLipsyncTake = async (index) => {
    try {
      await axios.delete(`${API}/scenes/${sceneId}/lipsync-takes/${index}`, { headers: getAuthHeaders() });
      // Refresh takes
      const updatedScene = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
      setLipsyncTakes(updatedScene.data.lipsync_takes || []);
      setActiveLipsyncIndex(updatedScene.data.active_lipsync_index || 0);
      setLipSyncVideoUrl(updatedScene.data.lipsync_video_url);
      setScene(prev => ({ ...prev, lipsync_video_url: updatedScene.data.lipsync_video_url }));
      toast.success("Lip sync take deleted");
    } catch (error) {
      toast.error("Failed to delete lip sync take");
    }
  };

  // Compute stage status for pipeline header
  const getStageStatus = () => {
    const hasComposition = selectedBackground || placedCharacters.length > 0;
    const hasPreview = hasComposition && scene?.composite_image_url;
    const hasAnimation = animationTakes.length > 0 || scene?.generated_video_url || uploadedSceneVideo;
    const hasLipsync = lipsyncTakes.length > 0 || scene?.lipsync_video_url;
    const hasStrategy = strategyChoice !== null;
    
    return {
      compose: { 
        completed: hasComposition 
      },
      strategy: {
        completed: hasStrategy
      },
      preview: {
        completed: hasPreview
      },
      animate: { 
        completed: hasAnimation 
      },
      lipsync: { 
        completed: hasLipsync 
      },
      print: { 
        completed: false 
      }
    };
  };

  const handleStageChange = async (newStage) => {
    // Validate stage transitions
    const stageStatus = getStageStatus();
    
    // Strategy is the first stage, always accessible
    if (newStage === "strategy") {
      setCurrentStage(newStage);
      return;
    }
    
    // Compose requires a strategy choice (or can be accessed directly for existing scenes)
    if (newStage === "compose" && !stageStatus.strategy.completed && !strategyChoice) {
      // Allow going to compose if strategy was already chosen or for returning users
      setStrategyChoice("compose");
    }
    
    // If user uploaded a video in strategy, allow going to Print directly
    if (uploadedSceneVideo) {
      if (newStage === "print") {
        setCurrentStage("print");
        return;
      }
      // Block other stages with a message
      if (["compose", "preview", "animate", "lipsync"].includes(newStage)) {
        toast.info("These steps are skipped when using uploaded video");
        return;
      }
    }
    
    // Allow going back to Print if user has lip sync takes or uploaded video
    if (newStage === "print" && (lipsyncTakes.length > 0 || uploadedSceneVideo)) {
      setCurrentStage("print");
      return;
    }
    
    // Allow going to Print if user has animation takes
    if (newStage === "print" && animationTakes.length > 0) {
      setCurrentStage("print");
      return;
    }
    
    if (newStage === "preview" && !stageStatus.compose.completed) {
      toast.error("Please set up your shot first (background + characters)");
      return;
    }
    
    if (newStage === "animate" && !stageStatus.compose.completed) {
      toast.error("Please set up your shot first (background + characters)");
      return;
    }
    
    if (newStage === "lipsync" && !stageStatus.animate.completed) {
      toast.error("Please generate an animation first");
      return;
    }
    
    if (newStage === "print" && !stageStatus.animate.completed) {
      toast.error("Please generate content first");
      return;
    }
    
    // Fetch prompt when entering Preview or Animate stage
    if ((newStage === "preview" || newStage === "animate") && !fullKlingPrompt) {
      try {
        const promptResponse = await axios.get(`${API}/scenes/${sceneId}/kling-prompt`, { headers: getAuthHeaders() });
        setFullKlingPrompt(promptResponse.data.prompt);
      } catch (e) {
        console.error('Failed to fetch prompt:', e);
      }
    }
    
    setCurrentStage(newStage);
  };

  // Handle strategy choice - compose or upload
  const handleStrategyChoice = (choice) => {
    setStrategyChoice(choice);
    if (choice === "compose") {
      setCurrentStage("compose");
    }
    // If upload, stay on strategy stage until video is uploaded
  };

  // Handle uploading a completed scene video
  const handleUploadSceneVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a video file");
      return;
    }
    
    setUploadingVideo(true);
    try {
      const asset = await uploadFileToS3(file, projectId, {
        type: 'video',
        name: file.name || "uploaded_scene_video"
      });
      const videoUrl = asset.file_url;
      setUploadedSceneVideo(videoUrl);
      setStrategyChoice("upload");
      
      // Also add the uploaded video as an animation take so it appears in the carousel
      const uploadedTake = {
        id: `upload-${Date.now()}`,
        video_url: videoUrl,
        thumbnail_url: null,
        prompt: "",
        mode: "upload",
        created_at: new Date().toISOString(),
        status: "completed"
      };
      const newTakes = [...animationTakes, uploadedTake];
      setAnimationTakes(newTakes);
      
      // Set as active take
      const newIndex = newTakes.length - 1;
      setActiveTakeIndex(newIndex);
      
      // Save to backend with uploaded_video_url
      await axios.put(`${API}/scenes/${sceneId}`, {
        animation_takes: newTakes,
        active_take_index: newIndex,
        uploaded_video_url: videoUrl
      }, { headers: getAuthHeaders() });
      
      // Extract thumbnail from uploaded video (fire and forget)
      axios.post(`${API}/scenes/${sceneId}/extract-thumbnail`).then(res => {
        if (res.data.success && res.data.thumbnail_url) {
          setScene(prev => ({ ...prev, thumbnail_url: res.data.thumbnail_url }));
        }
      }).catch(err => console.error('Failed to extract thumbnail:', err));
      
      toast.success("Video uploaded successfully!");
      
      // Skip intermediate steps and go directly to print
      setCurrentStage("print");
    } catch (error) {
      toast.error("Failed to upload video");
      console.error(error);
    } finally {
      setUploadingVideo(false);
    }
  };

  // Handle generate and auto-advance to animate stage (uses stored composite image)
  const handleGenerateAndAdvance = async () => {
    setGenerating(true);
    try {
      // Use the composite image that was captured when entering Preview stage
      // Pass the combined prompt built from the prompt builder fields
      const combinedPrompt = buildCombinedPrompt();
      await axios.post(`${API}/generate`, {
        scene_id: sceneId,
        custom_prompt: combinedPrompt,
        use_canvas_capture: true
      }, { headers: getAuthHeaders() });
      setScene(prev => ({ ...prev, status: "generating" }));
      setCurrentStage("animate");
      toast.info("Kling animation started - this may take 2-5 minutes...");
    } catch (error) {
      setGenerating(false);
      console.error("Generate error:", error);
      toast.error("Failed to start generation");
    }
  };

  // Handle O1 generation using the new Kling O1 reference-to-video model
  const handleO1Generate = async () => {
    if (!improvedPrompt.trim()) {
      toast.error("Please create a prompt first using 'Improve Prompt'");
      return;
    }
    
    setGenerating(true);
    try {
      // Filter o1Elements to only include elements that match current placed characters
      // This prevents sending stale elements from previous sessions
      const currentCharacterNames = placedCharacters.map(c => 
        (c.character_name || c.name || '').toLowerCase()
      );
      const filteredElements = o1Elements.filter(elem => {
        const elemName = (elem.name || '').toLowerCase();
        return currentCharacterNames.includes(elemName);
      });
      
      console.log(`Filtered elements: ${filteredElements.length} of ${o1Elements.length} (current chars: ${currentCharacterNames.join(', ')})`);
      
      // Save Preview state before generating (with filtered elements)
      await axios.put(`${API}/scenes/${sceneId}`, {
        action_direction: actionDirection,
        o1_elements: filteredElements,
        o1_style_images: styleImages,
        o1_improved_prompt: improvedPrompt,
        o1_negative_prompt: o1NegativePrompt
      }, { headers: getAuthHeaders() });
      
      // Build image_urls - composite as @Image1 (always used as start frame now)
      const imageUrls = [];
      if (scene?.composite_image_url) {
        imageUrls.push(scene.composite_image_url);
      }
      
      // Submit generation with filtered elements
      const response = await axios.post(`${API}/o1/generate`, {
        scene_id: sceneId,
        prompt: improvedPrompt,
        image_urls: imageUrls,
        elements: filteredElements,
        style_images: styleImages,
        video_reference: videoReference,
        use_start_frame: true, // Always use start frame
        duration: o1Duration,
        aspect_ratio: o1AspectRatio,
        negative_prompt: o1NegativePrompt
      }, { headers: getAuthHeaders() });
      
      if (response.data?.success) {
        const jobId = response.data.job_id;
        toast.success("O1 generation started!");
        setScene(prev => ({ ...prev, status: "generating" }));
        setCurrentStage("animate");
        
        // Start polling for status
        pollO1GenerationStatus(jobId);
      }
    } catch (error) {
      console.error("O1 generation error:", error);
      toast.error("Failed to start generation");
      setGenerating(false);
    }
  };

  // Handle Dialogue generation using Hedra
  const handleGenerateDialogue = async () => {
    if (!selectedVoiceForDialogue) {
      toast.error("Please select a voice file first");
      return;
    }
    
    setGenerating(true);
    setGeneratingDialogue(true);
    try {
      // Save dialogue state before generating
      await axios.put(`${API}/scenes/${sceneId}`, {
        action_direction: actionDirection,
        lipsync_selected_voice_id: selectedVoiceForDialogue
      }, { headers: getAuthHeaders() });
      
      // Use dialogue prompt or default
      const prompt = dialoguePrompt || DEFAULT_DIALOGUE_PROMPT;
      
      // Submit Hedra generation
      const response = await axios.post(`${API}/hedra/generate`, {
        scene_id: sceneId,
        voice_asset_id: selectedVoiceForDialogue,
        prompt: prompt,
        aspect_ratio: o1AspectRatio,
        resolution: "540p"
      }, { headers: getAuthHeaders() });
      
      if (response.data?.success) {
        const generationId = response.data.generation_id;
        toast.success("Dialogue generation started!");
        setScene(prev => ({ ...prev, status: "generating_dialogue" }));
        
        // Start polling for Hedra status
        pollHedraGenerationStatus(generationId);
      }
    } catch (error) {
      console.error("Hedra generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to start dialogue generation");
      setGenerating(false);
      setGeneratingDialogue(false);
    }
  };

  // Poll Hedra generation status
  const pollHedraGenerationStatus = async (generationId) => {
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/hedra/status/${sceneId}`, { headers: getAuthHeaders() });
        
        if (response.data.status === "complete") {
          // Refresh scene data to get the new video
          const updatedScene = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
          const takes = updatedScene.data.animation_takes || [];
          
          // Find the dialogue take that just completed
          const dialogueTakeIndex = takes.findIndex(t => t.hedra_generation_id === generationId || t.mode === 'dialogue');
          const newActiveTakeIndex = dialogueTakeIndex !== -1 ? dialogueTakeIndex : Math.max(0, takes.length - 1);
          
          // Update all state synchronously before navigation
          setScene(updatedScene.data);
          setAnimationTakes(takes);
          setActiveAnimationIndex(updatedScene.data.active_animation_index || 0);
          setActiveTakeIndex(newActiveTakeIndex);
          setGenerating(false);
          setGeneratingDialogue(false);
          
          toast.success("Dialogue video generated!");
          
          // Use setTimeout to ensure state updates are processed before navigation
          setTimeout(() => {
            setCurrentStage("print");
          }, 100);
        } else if (response.data.status === "error") {
          setGenerating(false);
          setGeneratingDialogue(false);
          toast.error(response.data.error || "Dialogue generation failed");
        } else {
          // Continue polling every 3 seconds
          setTimeout(poll, 3000);
        }
      } catch (error) {
        console.error("Error polling Hedra status:", error);
        setTimeout(poll, 5000); // Retry with longer interval on error
      }
    };
    
    poll();
  };

  // Poll O1 generation status
  const pollO1GenerationStatus = async (jobId) => {
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/o1/status/${jobId}`, { headers: getAuthHeaders() });
        
        if (response.data.status === "completed") {
          // Refresh scene data to get the new video
          const updatedScene = await axios.get(`${API}/scenes/${sceneId}`, { headers: getAuthHeaders() });
          setScene(updatedScene.data);
          setAnimationTakes(updatedScene.data.animation_takes || []);
          setActiveAnimationIndex(updatedScene.data.active_animation_index || 0);
          setGenerating(false);
          toast.success("Video generated!");
        } else if (response.data.status === "failed") {
          setGenerating(false);
          toast.error(response.data.message || "Generation failed");
        } else {
          // Continue polling every 3 seconds
          setTimeout(poll, 3000);
        }
      } catch (error) {
        console.error("Error polling status:", error);
        setTimeout(poll, 5000); // Retry with longer interval on error
      }
    };
    
    poll();
  };

  // Auto-populate O1 elements from placed characters
  const populateO1Elements = useCallback(() => {
    if (placedCharacters.length === 0) return;
    
    const newElements = placedCharacters.slice(0, 4).map((char) => {
      // Find the character profile if available
      const profile = characterProfiles.find(p => {
        // Match by character_profile_id or by name
        return p.id === char.character_profile_id || 
               p.name?.toLowerCase() === char.character_name?.toLowerCase() ||
               p.name?.toLowerCase() === char.name?.toLowerCase();
      });
      
      // Get reference images from profile
      const profileImages = profile?.reference_images || [];
      const primaryImage = profileImages.find(img => img.is_primary);
      const otherImages = profileImages.filter(img => !img.is_primary);
      
      return {
        character_profile_id: profile?.id || null,
        name: char.character_name || char.name,
        frontal_image_url: primaryImage?.file_url || char.file_url || "",
        reference_image_urls: otherImages.slice(0, 3).map(img => img.file_url),
        auto_tags: [char.character_name || char.name],
        user_confirmed: false
      };
    });
    
    setO1Elements(newElements);
  }, [placedCharacters, characterProfiles]);

  // Handle choose this take - advance to lipsync
  const handleChooseTake = (index) => {
    handleSelectAnimationTake(index);
    // Reset audio sync state when entering lipsync
    setAudioOffset(0);
    setIsPreviewPlaying(false);
    setAudioWaveform([]);
    setCurrentStage("lipsync");
  };

  // Handle choose lipsync take - advance to print
  const handleChooseLipsyncTake = (index) => {
    handleSelectLipsyncTake(index);
    setCurrentStage("print");
  };

  // Voice import handlers
  const fetchVoiceImportProjects = async () => {
    setLoadingVoiceImport(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/importable-projects`, { headers: getAuthHeaders() });
      setVoiceImportProjects(response.data || []);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoadingVoiceImport(false);
    }
  };

  const fetchVoiceImportAssets = async (sourceProjectId) => {
    setLoadingVoiceImport(true);
    try {
      const response = await axios.get(`${API}/projects/${sourceProjectId}/exportable-assets`, { headers: getAuthHeaders() });
      // Filter only voice assets
      const voiceAssets = (response.data || []).filter(a => a.type === 'voice');
      setVoiceImportAssets(voiceAssets);
    } catch (error) {
      toast.error("Failed to load voice files");
    } finally {
      setLoadingVoiceImport(false);
    }
  };

  const handleImportVoice = async (voiceAsset) => {
    try {
      toast.info("Importing voice file...");
      const response = await axios.post(`${API}/projects/${projectId}/import-assets`, {
        source_project_id: selectedVoiceImportProject,
        asset_ids: [voiceAsset.id]
      }, { headers: getAuthHeaders() });
      
      // Add imported asset to local state
      if (response.data.imported_assets?.length > 0) {
        const importedAsset = response.data.imported_assets[0];
        setAssets(prev => [...prev, importedAsset]);
        setSelectedVoiceForMerge(importedAsset.id);
        toast.success("Voice file imported!");
      }
      
      setShowVoiceImportDialog(false);
      setSelectedVoiceImportProject(null);
      setVoiceImportAssets([]);
    } catch (error) {
      toast.error("Failed to import voice file");
    }
  };

  // End frame management functions
  const handleAddEndFrame = () => {
    // Create a deep copy of current scene state for the end frame
    const endFrameCopy = placedCharacters.map(char => ({
      ...char,
    }));
    setEndFrameCharacters(endFrameCopy);
    setHasEndFrame(true);
    setEditingEndFrame(true);
    toast.success("End frame created - adjust character positions for the final frame");
  };

  const handleRemoveEndFrame = () => {
    setHasEndFrame(false);
    setEndFrameCharacters([]);
    setEditingEndFrame(false);
    setEndFramePreviewUrl(null);
    toast.info("End frame removed");
  };

  const handleToggleEndFrameEdit = () => {
    setEditingEndFrame(!editingEndFrame);
  };

  // Update end frame character position
  const handleEndFrameCharacterMove = (index, x, y) => {
    setEndFrameCharacters(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], x, y };
      return updated;
    });
  };

  // Update end frame character scale
  const handleEndFrameCharacterScale = (index, scale) => {
    setEndFrameCharacters(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], scale };
      return updated;
    });
  };

  // Generate end frame preview
  const handleGenerateEndFramePreview = async () => {
    try {
      const response = await axios.post(`${API}/preview-end-frame`, {
        scene_id: sceneId,
        end_frame_characters: endFrameCharacters
      }, { headers: getAuthHeaders() });
      setEndFramePreviewUrl(response.data.url);
      toast.success("End frame preview generated");
      return response.data.url;
    } catch (e) {
      toast.error("Failed to generate end frame preview");
      return null;
    }
  };

  // Generate waveform data from audio file
  const generateWaveform = useCallback(async (audioUrl) => {
    try {
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const rawData = audioBuffer.getChannelData(0);
      const samples = 100; // Number of bars in waveform
      const blockSize = Math.floor(rawData.length / samples);
      const waveformData = [];
      
      for (let i = 0; i < samples; i++) {
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[i * blockSize + j]);
        }
        waveformData.push(sum / blockSize);
      }
      
      // Normalize
      const max = Math.max(...waveformData);
      const normalized = waveformData.map(v => v / max);
      
      setAudioWaveform(normalized);
      setAudioDuration(audioBuffer.duration);
      audioContext.close();
    } catch (error) {
      console.error('Failed to generate waveform:', error);
      // Generate placeholder waveform
      setAudioWaveform(Array(100).fill(0).map(() => Math.random() * 0.5 + 0.2));
    }
  }, []);

  // Handle synced playback
  const togglePreviewPlayback = useCallback(() => {
    if (!videoPreviewRef.current) return;
    
    if (isPreviewPlaying) {
      // Stop playback
      videoPreviewRef.current.pause();
      if (audioPreviewRef.current) audioPreviewRef.current.pause();
      if (playheadIntervalRef.current) clearInterval(playheadIntervalRef.current);
      if (audioDelayTimeoutRef.current) clearTimeout(audioDelayTimeoutRef.current);
      setIsPreviewPlaying(false);
    } else {
      // Start playback from trim start
      const videoStartTime = trimStart;
      
      // Calculate audio position based on video position and offset
      const audioFilePosition = videoStartTime - audioOffset;
      
      console.log('[Playback] Starting playback', { 
        videoStartTime, 
        audioOffset, 
        audioFilePosition,
        trimStart, 
        trimEnd,
        audioDuration
      });
      
      // Set video position and start playing
      videoPreviewRef.current.currentTime = videoStartTime;
      setPlayheadPosition(videoStartTime);
      videoPreviewRef.current.play();
      setIsPreviewPlaying(true);
      
      // Handle initial audio state
      if (audioPreviewRef.current) {
        if (audioFilePosition < 0) {
          // Audio needs to be delayed
          audioPreviewRef.current.pause();
          audioPreviewRef.current.currentTime = 0;
        } else if (audioFilePosition < audioDuration) {
          // Try to start audio at position (may not work due to seeking limitations)
          audioPreviewRef.current.currentTime = audioFilePosition;
          audioPreviewRef.current.play().catch(() => {});
        }
      }
      
      // Set up interval to continuously sync audio with video
      playheadIntervalRef.current = setInterval(() => {
        if (!videoPreviewRef.current) return;
        
        const currentVideoTime = videoPreviewRef.current.currentTime;
        setPlayheadPosition(currentVideoTime);
        
        // Calculate where audio SHOULD be based on current video time
        const expectedAudioTime = currentVideoTime - audioOffset;
        
        if (audioPreviewRef.current) {
          const actualAudioTime = audioPreviewRef.current.currentTime;
          const isAudioPlaying = !audioPreviewRef.current.paused;
          
          if (expectedAudioTime < 0) {
            // Audio shouldn't have started yet
            if (isAudioPlaying) {
              audioPreviewRef.current.pause();
              audioPreviewRef.current.currentTime = 0;
            }
          } else if (expectedAudioTime >= audioDuration) {
            // Audio should have finished
            if (isAudioPlaying) {
              audioPreviewRef.current.pause();
            }
          } else {
            // Audio should be playing
            if (!isAudioPlaying) {
              // Start audio from expected position
              audioPreviewRef.current.currentTime = expectedAudioTime;
              audioPreviewRef.current.play().catch(() => {});
              console.log('[Sync] Started audio at:', expectedAudioTime);
            } else {
              // Check if audio is drifting from expected position
              const drift = Math.abs(actualAudioTime - expectedAudioTime);
              if (drift > 0.3) {
                // Significant drift - resync
                console.log('[Sync] Correcting drift. Expected:', expectedAudioTime.toFixed(2), 'Actual:', actualAudioTime.toFixed(2));
                audioPreviewRef.current.currentTime = expectedAudioTime;
              }
            }
          }
        }
        
        // Check if we've reached trim end
        const effectiveTrimEnd = trimEnd !== null ? trimEnd : videoDuration;
        if (currentVideoTime >= effectiveTrimEnd) {
          videoPreviewRef.current.pause();
          if (audioPreviewRef.current) audioPreviewRef.current.pause();
          clearInterval(playheadIntervalRef.current);
          if (audioDelayTimeoutRef.current) clearTimeout(audioDelayTimeoutRef.current);
          setIsPreviewPlaying(false);
        }
      }, 100); // Check sync every 100ms
    }
  }, [isPreviewPlaying, audioOffset, trimStart, trimEnd, videoDuration, audioDuration]);

  // Stop playback when video ends
  const handleVideoEnded = useCallback(() => {
    setIsPreviewPlaying(false);
    if (audioPreviewRef.current) audioPreviewRef.current.pause();
    if (playheadIntervalRef.current) clearInterval(playheadIntervalRef.current);
    if (audioDelayTimeoutRef.current) clearTimeout(audioDelayTimeoutRef.current);
  }, []);

  // Generate server-side synced preview using FFmpeg
  const handleGenerateSyncPreview = useCallback(async () => {
    if (!selectedVoiceForMerge) {
      toast.error("Please select an audio track first");
      return;
    }
    
    // Get the video URL to use
    let videoUrl = null;
    if (uploadedSceneVideo) {
      videoUrl = uploadedSceneVideo;
    } else if (animationTakes.length > 0 && animationTakes[activeAnimationIndex]?.video_url) {
      videoUrl = animationTakes[activeAnimationIndex].video_url;
    }
    
    if (!videoUrl) {
      toast.error("No video available for preview");
      return;
    }
    
    setGeneratingSyncPreview(true);
    setSyncPreviewUrl(null); // Clear previous preview
    
    try {
      const response = await axios.post(`${API}/scenes/${sceneId}/preview-sync`, {
        video_url: videoUrl,
        voice_asset_id: selectedVoiceForMerge,
        trim_start: trimStart,
        trim_end: trimEnd || videoDuration,
        audio_offset: audioOffset
      });
      
      if (response.data.success && response.data.preview_url) {
        // Add timestamp to bust cache
        const previewUrl = `${API}${response.data.preview_url.replace('/api', '')}?t=${Date.now()}`;
        setSyncPreviewUrl(previewUrl);
        toast.success("Preview generated! Playing merged video...");
      } else {
        toast.error("Failed to generate preview");
      }
    } catch (error) {
      console.error("Preview sync error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to generate preview";
      toast.error(errorMsg);
    } finally {
      setGeneratingSyncPreview(false);
    }
  }, [sceneId, selectedVoiceForMerge, uploadedSceneVideo, animationTakes, activeAnimationIndex, trimStart, trimEnd, videoDuration, audioOffset]);

  // Clear sync preview when timeline settings change
  const handleClearSyncPreview = useCallback(() => {
    setSyncPreviewUrl(null);
  }, []);

  // Load waveform when voice is selected
  useEffect(() => {
    if (selectedVoiceForMerge && currentStage === "lipsync") {
      // Check assets first, then actor recordings
      let voiceUrl = null;
      const voiceAsset = assets.find(a => a.id === selectedVoiceForMerge);
      if (voiceAsset?.file_url) {
        voiceUrl = voiceAsset.file_url;
      } else {
        // Check actor recordings in scene.audio_tracks
        const actorRecording = (scene?.audio_tracks || []).find(t => t.id === selectedVoiceForMerge);
        if (actorRecording?.file_url) {
          voiceUrl = actorRecording.file_url;
        }
      }
      if (voiceUrl) {
        generateWaveform(getAssetUrl(voiceUrl));
      }
    }
  }, [selectedVoiceForMerge, currentStage, assets, scene?.audio_tracks, generateWaveform]);

  const handleGenerateFromPreview = async () => {
    setPreviewMode(false);
    setGenerating(true);
    setMergedVideoUrl(null);
    setSelectedVoiceForMerge(null);
    try {
      await axios.post(`${API}/generate`, {
        scene_id: sceneId,
        custom_prompt: buildCombinedPrompt()
      });
      setScene(prev => ({ ...prev, status: "generating" }));
      toast.info("Kling animation started - this may take 2-5 minutes...");
    } catch (error) {
      setGenerating(false);
      toast.error("Failed to start generation");
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await handleSaveScene(true); // Silent save
      
      // First capture the canvas to create the composite image
      if (!canvasRef.current) {
        toast.error("Canvas not found");
        setGenerating(false);
        return;
      }
      
      toast.info("Capturing canvas for animation...");
      const canvas = await html2canvas(canvasRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#000000',
        scale: 2,
        logging: false,
        imageTimeout: 15000
      });
      
      // Convert to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
      
      // Upload canvas capture first
      const formData = new FormData();
      formData.append('scene_id', sceneId);
      formData.append('canvas_image', blob, 'canvas_capture.png');
      
      await axios.post(`${API}/preview-from-canvas`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Now start generation with the captured image and combined prompt
      await axios.post(`${API}/generate`, {
        scene_id: sceneId,
        custom_prompt: buildCombinedPrompt(),
        use_canvas_capture: true
      });
      setScene(prev => ({ ...prev, status: "generating" }));
      toast.info("Kling animation started - this may take 2-5 minutes...");
    } catch (error) {
      setGenerating(false);
      console.error("Generate error:", error);
      toast.error("Failed to start generation");
    }
  };

  const handleCancelGeneration = async () => {
    try {
      const response = await axios.post(`${API}/scenes/${sceneId}/cancel`);
      if (response.data.success) {
        setGenerating(false);
        setScene(prev => ({ ...prev, status: "cancelled" }));
        toast.info("Generation cancelled");
      }
    } catch (error) {
      toast.error("Failed to cancel generation");
    }
  };

  const handleCharacterAdd = (character) => {
    const newChar = {
      asset_id: character.id,
      x: 50,
      y: 60,
      scale: 1,
      opacity: 100,
      rotation: 0,
      blur: 0,
      name: character.name,
      character_name: character.name.replace(/\.[^/.]+$/, "").toUpperCase(),
      file_url: character.file_url,
      visible: true,
      flipX: false,
      flipY: false
    };
    
    // Add to the correct frame based on editing mode
    if (editingEndFrame && hasEndFrame) {
      setEndFrameCharacters(prev => [newChar, ...prev]);
    } else {
      setPlacedCharacters(prev => [newChar, ...prev]);
    }
    setSelectedCharacter(0);
  };

  const handleCharacterMove = (index, x, y) => {
    setPlacedCharacters(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], x, y };
      return updated;
    });
  };

  const handleCharacterScale = (index, scale) => {
    setPlacedCharacters(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], scale: Math.max(0.1, Math.min(10, scale)) };
      return updated;
    });
  };

  const handleCharacterOpacity = (index, opacity) => {
    setPlacedCharacters(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], opacity: Math.max(0, Math.min(100, opacity)) };
      return updated;
    });
  };

  const handleCharacterRotation = (index, rotation) => {
    setPlacedCharacters(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], rotation: rotation % 360 };
      return updated;
    });
  };

  const handleCharacterNameChange = (index, newName) => {
    const activeChars = getActiveCharacters();
    const updated = [...activeChars];
    updated[index] = { ...updated[index], character_name: newName.toUpperCase() };
    setActiveCharacters(updated);
  };

  const handleCharacterVisibility = (index) => {
    const activeChars = getActiveCharacters();
    const updated = [...activeChars];
    updated[index] = { ...updated[index], visible: updated[index].visible === false ? true : false };
    setActiveCharacters(updated);
  };

  const handleCharacterFlip = (index, axis) => {
    const activeChars = getActiveCharacters();
    const updated = [...activeChars];
    if (axis === 'horizontal') {
      updated[index] = { ...updated[index], flipX: !updated[index].flipX };
    } else {
      updated[index] = { ...updated[index], flipY: !updated[index].flipY };
    }
    setActiveCharacters(updated);
  };

  // Get the active characters array based on editing mode
  const getActiveCharacters = () => editingEndFrame && hasEndFrame ? endFrameCharacters : placedCharacters;
  const setActiveCharacters = (updater) => {
    if (editingEndFrame && hasEndFrame) {
      if (typeof updater === 'function') {
        setEndFrameCharacters(updater);
      } else {
        setEndFrameCharacters(updater);
      }
    } else {
      if (typeof updater === 'function') {
        setPlacedCharacters(updater);
      } else {
        setPlacedCharacters(updater);
      }
    }
  };

  const handleLayerMoveUp = (index) => {
    const activeChars = getActiveCharacters();
    if (index <= 0) return;
    const updated = [...activeChars];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setActiveCharacters(updated);
    if (selectedCharacter === index) setSelectedCharacter(index - 1);
    else if (selectedCharacter === index - 1) setSelectedCharacter(index);
  };

  const handleLayerMoveDown = (index) => {
    const activeChars = getActiveCharacters();
    if (index >= activeChars.length - 1) return;
    const updated = [...activeChars];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setActiveCharacters(updated);
    if (selectedCharacter === index) setSelectedCharacter(index + 1);
    else if (selectedCharacter === index + 1) setSelectedCharacter(index);
  };

  const handleCharacterRemove = (index) => {
    const activeChars = getActiveCharacters();
    setActiveCharacters(activeChars.filter((_, i) => i !== index));
    if (selectedCharacter === index) setSelectedCharacter(null);
    else if (selectedCharacter > index) setSelectedCharacter(selectedCharacter - 1);
  };

  const handleAudioAdd = (audio) => {
    const newTrack = {
      asset_id: audio.id,
      name: audio.name,
      file_url: audio.file_url,
      start_time: 0,
      duration: 10
    };
    setAudioTracks([...audioTracks, newTrack]);
  };

  const handleAudioRemove = (index) => {
    setAudioTracks(audioTracks.filter((_, i) => i !== index));
  };

  // Fixed resize handlers - corrected direction for all corners
  const handleResizeStart = (e, index, corner) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing({ index, corner });
    setIsInteracting(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scale: placedCharacters[index].scale
    };
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizing || !resizeStartRef.current) return;
    const { index, corner } = resizing;
    const startData = resizeStartRef.current;
    const deltaX = e.clientX - startData.x;
    const deltaY = e.clientY - startData.y;
    
    // Calculate scale change based on corner
    let scaleDelta = 0;
    if (corner === 'se') {
      // Bottom-right: drag right/down = bigger
      scaleDelta = (deltaX + deltaY) / 200;
    } else if (corner === 'sw') {
      // Bottom-left: drag left/down = bigger (so negate deltaX)
      scaleDelta = (-deltaX + deltaY) / 200;
    } else if (corner === 'ne') {
      // Top-right: drag right/up = bigger (so negate deltaY)
      scaleDelta = (deltaX - deltaY) / 200;
    } else if (corner === 'nw') {
      // Top-left: drag left/up = bigger (so negate both)
      scaleDelta = (-deltaX - deltaY) / 200;
    }
    
    const newScale = Math.max(0.1, Math.min(10, startData.scale + scaleDelta));
    
    // Use CSS transform for visual feedback instead of updating React state
    setVisualResizeScale({ index, scale: newScale });
  }, [resizing]);

  const handleResizeEnd = useCallback(() => {
    // Apply the final scale to React state
    if (resizing && visualResizeScale && resizeStartRef.current) {
      const { index } = resizing;
      setPlacedCharacters(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], scale: visualResizeScale.scale };
        return updated;
      });
      pendingSaveRef.current = true;
    }
    setResizing(null);
    setVisualResizeScale(null);
    setIsInteracting(false);
    resizeStartRef.current = null;
  }, [resizing, visualResizeScale]);

  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);

  // Character drag handlers - only works when selected via layer list
  const handleCharacterDragStart = (e, index) => {
    if (resizing || selectedCharacter !== index) return;
    e.preventDefault();
    e.stopPropagation();
    setDraggingCharacter(index);
    setIsInteracting(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    charDragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      charX: placedCharacters[index].x,
      charY: placedCharacters[index].y,
      canvasWidth: rect.width,
      canvasHeight: rect.height
    };
  };

  const handleCharacterDragMove = useCallback((e) => {
    if (draggingCharacter === null || !charDragStartRef.current) return;
    const { mouseX, mouseY, charX, charY, canvasWidth, canvasHeight } = charDragStartRef.current;
    const deltaX = ((e.clientX - mouseX) / canvasWidth) * 100;
    const deltaY = ((e.clientY - mouseY) / canvasHeight) * 100;
    
    // Use CSS transform offset for visual feedback instead of updating React state
    setVisualDragOffset({ x: deltaX, y: deltaY });
  }, [draggingCharacter]);

  const handleCharacterDragEnd = useCallback(() => {
    // Apply the final position to React state
    if (draggingCharacter !== null && charDragStartRef.current) {
      const { charX, charY } = charDragStartRef.current;
      const newX = charX + visualDragOffset.x;
      const newY = charY + visualDragOffset.y;
      
      if (editingEndFrame && hasEndFrame) {
        setEndFrameCharacters(prev => {
          const updated = [...prev];
          updated[draggingCharacter] = { ...updated[draggingCharacter], x: newX, y: newY };
          return updated;
        });
      } else {
        setPlacedCharacters(prev => {
          const updated = [...prev];
          updated[draggingCharacter] = { ...updated[draggingCharacter], x: newX, y: newY };
          return updated;
        });
      }
      pendingSaveRef.current = true;
    }
    setDraggingCharacter(null);
    setVisualDragOffset({ x: 0, y: 0 });
    setIsInteracting(false);
    charDragStartRef.current = null;
  }, [draggingCharacter, visualDragOffset, editingEndFrame, hasEndFrame]);

  useEffect(() => {
    if (draggingCharacter !== null) {
      window.addEventListener('mousemove', handleCharacterDragMove);
      window.addEventListener('mouseup', handleCharacterDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleCharacterDragMove);
        window.removeEventListener('mouseup', handleCharacterDragEnd);
      };
    }
  }, [draggingCharacter, handleCharacterDragMove, handleCharacterDragEnd]);

  // Property drag handlers (for scale, opacity, rotation, position)
  // Supports both vertical (up/down) and horizontal (left/right) dragging
  const handlePropertyDragStart = (e, index, property) => {
    e.preventDefault();
    setDraggingProperty({ index, property });
    propertyDragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      value: placedCharacters[index][property]
    };
  };

  const handlePropertyDragMove = useCallback((e) => {
    if (!draggingProperty || !propertyDragStartRef.current) return;
    const { index, property } = draggingProperty;
    const { x: startX, y: startY, value: startValue } = propertyDragStartRef.current;
    const deltaY = startY - e.clientY; // Up = positive
    const deltaX = e.clientX - startX; // Right = positive
    // Combine both directions (right and up = increase)
    const delta = deltaY + deltaX;
    
    let newValue;
    if (property === 'scale') {
      newValue = Math.max(0.1, Math.min(10, startValue + delta * 0.005));
    } else if (property === 'opacity') {
      newValue = Math.max(0, Math.min(100, startValue + delta * 0.5));
    } else if (property === 'rotation') {
      newValue = (startValue + delta) % 360;
    } else if (property === 'blur') {
      newValue = Math.max(0, Math.min(20, startValue + delta * 0.1));
    } else if (property === 'x' || property === 'y') {
      newValue = startValue + (property === 'y' ? -delta : delta) * 0.2;
    }
    
    // Update the correct array based on editing mode
    if (editingEndFrame && hasEndFrame) {
      setEndFrameCharacters(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [property]: newValue };
        return updated;
      });
    } else {
      setPlacedCharacters(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [property]: newValue };
        return updated;
      });
    }
  }, [draggingProperty, editingEndFrame, hasEndFrame]);

  const handlePropertyDragEnd = useCallback(() => {
    setDraggingProperty(null);
    propertyDragStartRef.current = null;
  }, []);

  useEffect(() => {
    if (draggingProperty) {
      window.addEventListener('mousemove', handlePropertyDragMove);
      window.addEventListener('mouseup', handlePropertyDragEnd);
      return () => {
        window.removeEventListener('mousemove', handlePropertyDragMove);
        window.removeEventListener('mouseup', handlePropertyDragEnd);
      };
    }
  }, [draggingProperty, handlePropertyDragMove, handlePropertyDragEnd]);

  // Background pan handlers
  const handleBackgroundPanStart = (e) => {
    if (e.target !== canvasRef.current && !e.target.classList.contains('canvas-background')) return;
    e.preventDefault();
    setIsPanningBackground(true);
    setIsInteracting(true);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: backgroundPan.x,
      panY: backgroundPan.y
    };
  };

  const handleBackgroundPanMove = useCallback((e) => {
    if (!isPanningBackground || !panStartRef.current) return;
    const { x, y, panX, panY } = panStartRef.current;
    const deltaX = e.clientX - x;
    const deltaY = e.clientY - y;
    setBackgroundPan({
      x: panX + deltaX * 0.1,
      y: panY + deltaY * 0.1
    });
  }, [isPanningBackground]);

  const handleBackgroundPanEnd = useCallback(() => {
    setIsPanningBackground(false);
    setIsInteracting(false);
    pendingSaveRef.current = true;
    panStartRef.current = null;
  }, []);

  useEffect(() => {
    if (isPanningBackground) {
      window.addEventListener('mousemove', handleBackgroundPanMove);
      window.addEventListener('mouseup', handleBackgroundPanEnd);
      return () => {
        window.removeEventListener('mousemove', handleBackgroundPanMove);
        window.removeEventListener('mouseup', handleBackgroundPanEnd);
      };
    }
  }, [isPanningBackground, handleBackgroundPanMove, handleBackgroundPanEnd]);

  // Deferred auto-save: Save scene after interaction ends
  useEffect(() => {
    if (!isInteracting && pendingSaveRef.current && sceneId) {
      pendingSaveRef.current = false;
      // Small delay to batch any final state updates
      const saveTimeout = setTimeout(() => {
        handleSaveScene(true); // silent save
      }, 500);
      return () => clearTimeout(saveTimeout);
    }
  }, [isInteracting, sceneId]);

  const getAssetsByType = (type) => {
    let filtered = assets.filter(a => a.type === type);
    if (assetSearch.trim()) {
      filtered = filtered.filter(a => 
        a.name.toLowerCase().includes(assetSearch.toLowerCase())
      );
    }
    return filtered;
  };

  const getBackgroundAsset = () => assets.find(a => a.id === selectedBackground);

  // toggleAssetExpanded kept as a simple helper
  const toggleAssetExpanded = (type) => {
    setExpandedAssetTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // Handler for property changes from DraggableProperty
  const handlePropertyChangeFromDraggable = (index, property, value) => {
    if (editingEndFrame && hasEndFrame) {
      setEndFrameCharacters(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [property]: value };
        return updated;
      });
    } else {
      setPlacedCharacters(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [property]: value };
        return updated;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:h-screen w-screen max-w-[100vw] bg-[#09090b] flex flex-col overflow-x-hidden overflow-y-auto lg:overflow-hidden animate-fadeIn box-border">
      {/* Smart Import Modal for characters */}
      <SmartImportModal
        open={showSmartImport}
        onClose={() => {
          setShowSmartImport(false);
          setSmartImportFiles([]);
        }}
        files={smartImportFiles}
        existingCharacters={characterProfiles}
        onConfirm={handleSmartImportConfirm}
        projectId={projectId}
      />
      
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-sm px-3 lg:px-4 py-2 lg:py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 lg:gap-4 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/project/${projectId}`)}
            className="text-zinc-400 hover:text-white w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-sm lg:text-lg font-semibold text-white truncate max-w-[120px] lg:max-w-[200px]">{scene?.name}</h1>
              {hasUnsavedChanges && (
                <span className="text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded flex-shrink-0">unsaved</span>
              )}
            </div>
            <span className={`status-badge status-${scene?.status} text-xs`}>{scene?.status}</span>
          </div>
        </div>
        
        {/* Right side - Navigation + Actions (fixed position) */}
        <div className="flex items-center gap-2 lg:gap-4 flex-shrink-0">
          {/* Shot Navigation - Always right-aligned */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateToScene('prev')}
              disabled={!hasPreviousScene}
              className="text-zinc-400 hover:text-white disabled:opacity-30 gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden lg:inline">Previous</span>
            </Button>
            <span className="text-xs text-zinc-500 px-2 min-w-[80px] text-center">
              Shot {currentSceneIndex + 1} of {projectScenes.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateToScene('next')}
              disabled={!hasNextScene}
              className="text-zinc-400 hover:text-white disabled:opacity-30 gap-1"
            >
              <span className="hidden lg:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        
          {/* Action Buttons */}
          <div className="flex items-center gap-1 lg:gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSaveWithComposite}
              disabled={savingWithComposite}
              className="border-zinc-700 text-zinc-300 hover:text-white gap-1 lg:gap-2 px-2 lg:px-3"
            >
              {savingWithComposite ? (
                <><Loader2 className="w-4 h-4 animate-spin" /><span className="hidden sm:inline">Saving...</span></>
              ) : (
                <><Save className="w-4 h-4" /><span className="hidden sm:inline">Save</span></>
              )}
            </Button>
            {(generating || scene?.status === "generating") && (
              <Button onClick={handleCancelGeneration} variant="destructive" size="sm" className="gap-1">
                <Square className="w-3 h-3" /> Stop
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Pipeline Header */}
      <PipelineHeader
        currentStage={currentStage}
        onStageChange={handleStageChange}
        stageStatus={getStageStatus()}
        skippedStages={uploadedSceneVideo && !lipsyncUnlockedForUpload ? ["compose", "preview", "animate", "lipsync"] : uploadedSceneVideo ? ["compose", "preview", "animate"] : []}
        forceAccessibleStages={
          lipsyncTakes.length > 0 ? ["print", "lipsync"] : 
          uploadedSceneVideo && lipsyncUnlockedForUpload ? ["print", "lipsync"] :
          uploadedSceneVideo ? ["print"] : 
          animationTakes.length > 0 ? ["print", "animate"] :
          []
        }
        disabled={false}
        generating={generating || scene?.status === "generating"}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row gap-0 overflow-auto lg:overflow-hidden min-w-0">
        {/* Left Panel - Asset Library (Only visible in Compose stage) */}
        {currentStage === "compose" && (
        <div className={`${assetLibraryCollapsed ? 'w-12' : 'w-64 min-w-[200px]'} border-b lg:border-b-0 lg:border-r border-zinc-800 bg-zinc-900/30 flex flex-col min-h-[200px] lg:min-h-0 lg:h-full overflow-hidden transition-all duration-300 shrink-0`}>
          {assetLibraryCollapsed ? (
            /* Collapsed state - just show expand button and vertical icons */
            <div className="flex flex-col items-center py-2 gap-2 h-full">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setAssetLibraryCollapsed(false)}
                className="w-8 h-8 p-0 text-zinc-400 hover:text-white"
                title="Expand asset library"
              >
                <PanelLeft className="w-4 h-4" />
              </Button>
              <div className="flex flex-col items-center gap-3 mt-4">
                <Image className="w-4 h-4 text-zinc-500" title="Sets" />
                <User className="w-4 h-4 text-zinc-500" title="Characters" />
                <Box className="w-4 h-4 text-zinc-500" title="Items" />
                <Mic className="w-4 h-4 text-zinc-500" title="Voice" />
                <Music className="w-4 h-4 text-zinc-500" title="Music" />
              </div>
            </div>
          ) : (
            /* Expanded state - full asset library */
            <>
              <div className="flex items-center justify-between px-2 pt-2 shrink-0">
                <span className="text-xs text-zinc-500 font-medium">Assets</span>
                <div className="flex items-center gap-1">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6 p-0 text-zinc-400 hover:text-red-400"
                        title="Delete all assets"
                        disabled={deletingAllAssets || assets.length === 0}
                      >
                        {deletingAllAssets ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete All Assets?</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                          This will permanently delete all {assets.length} assets from this project.
                          <span className="block mt-2 text-red-400 font-medium">
                            This action cannot be reversed.
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteAllAssets}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Delete All Assets
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setAssetLibraryCollapsed(true)}
                    className="w-6 h-6 p-0 text-zinc-400 hover:text-white"
                    title="Collapse asset library"
                  >
                    <PanelLeftClose className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <Tabs defaultValue="background" className="flex-1 flex flex-col h-full overflow-hidden">
                <TabsList className="bg-zinc-800/50 mx-2 mt-1 p-1 h-auto flex-wrap gap-1 shrink-0">
                  <TabsTrigger value="background" className="text-xs flex-1 data-[state=active]:bg-indigo-500" title="Sets">
                    <Image className="w-3 h-3" />
                  </TabsTrigger>
                  <TabsTrigger value="character" className="text-xs flex-1 data-[state=active]:bg-indigo-500" title="Characters">
                    <User className="w-3 h-3" />
                  </TabsTrigger>
                  <TabsTrigger value="prop" className="text-xs flex-1 data-[state=active]:bg-indigo-500" title="Items">
                    <Box className="w-3 h-3" />
                  </TabsTrigger>
                  <TabsTrigger value="voice" className="text-xs flex-1 data-[state=active]:bg-indigo-500" title="Voice Files">
                    <Mic className="w-3 h-3" />
                  </TabsTrigger>
                  <TabsTrigger value="music" className="text-xs flex-1 data-[state=active]:bg-indigo-500" title="Music">
                    <Music className="w-3 h-3" />
                  </TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-hidden relative">
                  <TabsContent value="background" className="absolute inset-0 p-2 m-0 data-[state=inactive]:hidden flex flex-col">
                    <AssetPanel 
                      type="background" 
                      label="Sets" 
                      icon={Image} 
                      acceptType="image/*" 
                      aspectRatio="video"
                      scene={scene}
                      assets={assets}
                      assetSearch={assetSearch}
                      setAssetSearch={setAssetSearch}
                      selectedBackground={selectedBackground}
                      setSelectedBackground={setSelectedBackground}
                      expandedAssetTypes={expandedAssetTypes}
                      toggleAssetExpanded={toggleAssetExpanded}
                      uploadingType={uploadingType}
                      handleUploadAsset={handleUploadAsset}
                      handleOpenImport={handleOpenImport}
                      handleCharacterAdd={handleCharacterAdd}
                      handleAudioAdd={handleAudioAdd}
                    />
                  </TabsContent>

                  <TabsContent value="character" className="absolute inset-0 p-2 m-0 data-[state=inactive]:hidden flex flex-col">
                    <AssetPanel 
                      type="character" 
                      label="Characters" 
                      icon={User} 
                      acceptType="image/*" 
                      onAdd={handleCharacterAdd}
                      scene={scene}
                      assets={assets}
                      assetSearch={assetSearch}
                      setAssetSearch={setAssetSearch}
                      selectedBackground={selectedBackground}
                      setSelectedBackground={setSelectedBackground}
                      expandedAssetTypes={expandedAssetTypes}
                      toggleAssetExpanded={toggleAssetExpanded}
                      uploadingType={uploadingType}
                      handleUploadAsset={handleUploadAsset}
                      handleOpenImport={handleOpenImport}
                      handleCharacterAdd={handleCharacterAdd}
                      handleAudioAdd={handleAudioAdd}
                      characterProfiles={characterProfiles}
                      selectedCharacterProfile={selectedCharacterProfile}
                      setSelectedCharacterProfile={setSelectedCharacterProfile}
                      onBulkUpload={(files) => {
                        setSmartImportFiles(files);
                        setShowSmartImport(true);
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="prop" className="absolute inset-0 p-2 m-0 data-[state=inactive]:hidden flex flex-col">
                    <AssetPanel 
                      type="prop" 
                      label="Items" 
                      icon={Box} 
                      acceptType="image/*" 
                      onAdd={handleCharacterAdd}
                      scene={scene}
                      assets={assets}
                      assetSearch={assetSearch}
                      setAssetSearch={setAssetSearch}
                      selectedBackground={selectedBackground}
                      setSelectedBackground={setSelectedBackground}
                      expandedAssetTypes={expandedAssetTypes}
                      toggleAssetExpanded={toggleAssetExpanded}
                      uploadingType={uploadingType}
                      handleUploadAsset={handleUploadAsset}
                      handleOpenImport={handleOpenImport}
                      handleCharacterAdd={handleCharacterAdd}
                      handleAudioAdd={handleAudioAdd}
                    />
                  </TabsContent>

                  <TabsContent value="voice" className="absolute inset-0 p-2 m-0 data-[state=inactive]:hidden flex flex-col">
                    <AssetPanel 
                      type="voice" 
                      label="Voice Files" 
                      icon={Mic} 
                      acceptType="audio/*" 
                      onAdd={handleAudioAdd}
                      scene={scene}
                      assets={assets}
                      assetSearch={assetSearch}
                      setAssetSearch={setAssetSearch}
                      selectedBackground={selectedBackground}
                      setSelectedBackground={setSelectedBackground}
                      expandedAssetTypes={expandedAssetTypes}
                      toggleAssetExpanded={toggleAssetExpanded}
                      uploadingType={uploadingType}
                      handleUploadAsset={handleUploadAsset}
                      handleOpenImport={handleOpenImport}
                      handleCharacterAdd={handleCharacterAdd}
                      handleAudioAdd={handleAudioAdd}
                    />
                  </TabsContent>

                  <TabsContent value="music" className="absolute inset-0 p-2 m-0 data-[state=inactive]:hidden flex flex-col">
                    <AssetPanel 
                      type="music" 
                      label="Music" 
                      icon={Music} 
                      acceptType="audio/*" 
                      onAdd={handleAudioAdd}
                      scene={scene}
                      assets={assets}
                      assetSearch={assetSearch}
                      setAssetSearch={setAssetSearch}
                      selectedBackground={selectedBackground}
                      setSelectedBackground={setSelectedBackground}
                      expandedAssetTypes={expandedAssetTypes}
                      toggleAssetExpanded={toggleAssetExpanded}
                      uploadingType={uploadingType}
                      handleUploadAsset={handleUploadAsset}
                      handleOpenImport={handleOpenImport}
                      handleCharacterAdd={handleCharacterAdd}
                      handleAudioAdd={handleAudioAdd}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </>
          )}
        </div>
        )}

        {/* Center - Canvas & Layers (Compose) OR Preview/Animate/LipSync/Export Views */}
        <div className="flex-1 flex flex-col min-h-[400px] lg:min-h-0 overflow-hidden min-w-0">
          
          {/* COMPOSE STAGE: Canvas with layers */}
          {currentStage === "compose" && (
            <>
              {/* Canvas Area */}
              <div className={`p-2 lg:p-4 flex flex-col items-center justify-center ${showLayersModal ? 'flex-1' : 'shrink-0'}`}>
                <div className="relative w-full h-full flex items-center justify-center">
                  {/* End Frame Mode Indicator */}
                  {editingEndFrame && hasEndFrame && (
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 z-50 bg-orange-600/90 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-lg flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Editing End Frame
                    </div>
                  )}
                  
                  <div
                    ref={canvasRef}
                    data-testid="animation-canvas"
                    className={`canvas-area rounded-lg relative border overflow-hidden bg-zinc-950 ${editingEndFrame ? 'ring-2 ring-orange-500/50' : 'border-zinc-800'}`}
                    style={{ 
                      aspectRatio: "16/9",
                      width: '100%',
                      maxWidth: 'calc((100vh - 200px) * 16 / 9)',
                      maxHeight: 'calc(100vh - 200px)',
                      cursor: isPanningBackground ? 'grabbing' : 'grab'
                    }}
                    onMouseDown={handleBackgroundPanStart}
                  >
                    {/* Background with zoom/pan */}
                    {selectedBackground && getBackgroundAsset() && getBackgroundAsset().file_url && (
                      <img
                        src={getAssetUrl(getBackgroundAsset().file_url)}
                        alt="Background"
                        crossOrigin="anonymous"
                        className="canvas-background absolute w-full h-full object-cover pointer-events-none"
                        style={{
                          transform: `scale(${backgroundZoom / 100}) translate(${backgroundPan.x}%, ${backgroundPan.y}%)`,
                          transformOrigin: 'center',
                          filter: backgroundBlur > 0 ? `blur(${backgroundBlur}px)` : 'none',
                          zIndex: 0
                        }}
                        onError={(e) => {
                          console.error('Background image failed to load:', e.target.src);
                        }}
                      />
                    )}
                    {/* Fallback: Show message if background ID is set but asset not found */}
                    {selectedBackground && (!getBackgroundAsset() || !getBackgroundAsset().file_url) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 text-zinc-400 text-sm z-10">
                        <div className="text-center">
                          <p>Background not found</p>
                          <p className="text-xs text-zinc-500 mt-1">Select a set from the left panel</p>
                        </div>
                      </div>
                    )}

                    {/* Placed Characters */}
                    {[...(editingEndFrame && hasEndFrame ? endFrameCharacters : placedCharacters)].reverse().map((char, reverseIndex) => {
                      const activeCharacters = editingEndFrame && hasEndFrame ? endFrameCharacters : placedCharacters;
                      const index = activeCharacters.length - 1 - reverseIndex;
                      // Character size: at scale 1.0, character height = ~100% of canvas height
                      // For 16:9 canvas (1920x1080), this means width = 56.25% of canvas width
                      // Using 55% as base to give slight margin
                      const sizePercent = 55 * char.scale;
                      const isVisible = char.visible !== false;
                      const isSelected = selectedCharacter === index;
                      
                      // Don't render if not visible or if no file_url (prevents empty squares)
                      if (!isVisible || !char.file_url) return null;
                      
                      // Calculate visual transforms during interactions
                      const isDragging = draggingCharacter === index;
                      const isResizing = resizing?.index === index;
                      
                      // Apply visual drag offset (CSS transform) during drag
                      const visualX = isDragging ? char.x + visualDragOffset.x : char.x;
                      const visualY = isDragging ? char.y + visualDragOffset.y : char.y;
                      
                      // Apply visual scale during resize
                      const visualScale = isResizing && visualResizeScale ? visualResizeScale.scale : char.scale;
                      const visualSizePercent = 55 * visualScale;
                      
                      return (
                        <div
                          key={char.asset_id + '-' + index + (editingEndFrame ? '-end' : '-start')}
                          onMouseDown={(e) => {
                            if (editingEndFrame && hasEndFrame) {
                              // Handle end frame character drag
                              e.stopPropagation();
                              setSelectedCharacter(index);
                              setIsInteracting(true);
                              const rect = canvasRef.current.getBoundingClientRect();
                              charDragStartRef.current = {
                                mouseX: e.clientX,
                                mouseY: e.clientY,
                                charX: char.x,
                                charY: char.y,
                                canvasWidth: rect.width,
                                canvasHeight: rect.height
                              };
                              setDraggingCharacter(index);
                            } else {
                              handleCharacterDragStart(e, index);
                            }
                          }}
                          style={{
                            left: `${visualX}%`,
                            top: `${visualY}%`,
                            transform: `translate(-50%, -50%) rotate(${char.rotation || 0}deg)`,
                            width: `${visualSizePercent}%`,
                            aspectRatio: '1',
                            opacity: (char.opacity ?? 100) / 100,
                            filter: (char.blur ?? 0) > 0 ? `blur(${char.blur}px)` : 'none',
                            cursor: isSelected ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            zIndex: isDragging ? 1000 : (activeCharacters.length - index),
                            pointerEvents: isSelected ? 'auto' : 'none',
                            willChange: isDragging || isResizing ? 'transform, width' : 'auto'
                          }}
                          className={`absolute ${isSelected ? "ring-2 ring-indigo-500 ring-offset-2 ring-offset-transparent rounded" : ""}`}
                        >
                          {char.file_url && (
                            <img
                              src={getAssetUrl(char.file_url)}
                              alt={char.name || 'Character'}
                              crossOrigin="anonymous"
                              className="w-full h-full object-contain pointer-events-none"
                              draggable={false}
                              style={{
                                transform: `${char.flipX ? 'scaleX(-1)' : ''} ${char.flipY ? 'scaleY(-1)' : ''}`.trim() || 'none'
                              }}
                            />
                          )}
                          
                          {/* Resize Handles */}
                          {isSelected && (
                            <>
                              {['nw', 'ne', 'sw', 'se'].map((corner) => (
                                <div
                                  key={corner}
                                  onMouseDown={(e) => handleResizeStart(e, index, corner)}
                                  className={`absolute w-3 h-3 bg-indigo-500 border-2 border-white rounded-sm z-10
                                    ${corner.includes('n') ? '-top-1.5' : '-bottom-1.5'}
                                    ${corner.includes('w') ? '-left-1.5' : '-right-1.5'}
                                  `}
                                  style={{ 
                                    cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                                    pointerEvents: 'auto'
                                  }}
                                />
                              ))}
                            </>
                          )}
                        </div>
                      );
                    })}

                    {/* Grid Overlay */}
                    {showGrid && (
                      <div 
                        className="absolute inset-0 pointer-events-none z-50"
                        style={{
                          backgroundImage: `
                            linear-gradient(to right, rgba(99, 102, 241, 0.5) ${gridLineWidth}px, transparent ${gridLineWidth}px),
                            linear-gradient(to bottom, rgba(99, 102, 241, 0.5) ${gridLineWidth}px, transparent ${gridLineWidth}px)
                          `,
                          backgroundSize: `${100 / gridSize}% ${100 / gridSize}%`
                        }}
                        data-testid="canvas-grid-overlay"
                      />
                    )}

                    {/* Empty State */}
                    {!selectedBackground && placedCharacters.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center text-zinc-500 pointer-events-none">
                        <div className="text-center">
                          <Film className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Select a background and add characters</p>
                        </div>
                      </div>
                    )}

                    {/* Generating Preview Overlay */}
                    {generatingPreview && (
                      <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 z-[100]">
                        <div className="text-center">
                          <Loader2 className="w-16 h-16 mx-auto mb-4 text-indigo-500 animate-spin" />
                          <p className="text-white font-medium">Generating Preview...</p>
                          <p className="text-sm text-zinc-400 mt-1">Please wait...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* STRATEGY STAGE: Choose workflow - Compose or Upload */}
          {currentStage === "strategy" && (
            <StrategyStage
              onStrategyChoice={handleStrategyChoice}
              onUploadSceneVideo={handleUploadSceneVideo}
              uploadingVideo={uploadingVideo}
            />
          )}

          {/* PREVIEW STAGE: O1 Reference-to-Video Workflow */}
          {currentStage === "preview" && (
            <PreviewStage
              scene={scene}
              sceneId={sceneId}
              projectId={projectId}
              setCurrentStage={setCurrentStage}
              getAuthHeaders={getAuthHeaders}
              // O1 State
              o1Elements={o1Elements}
              setO1Elements={setO1Elements}
              styleImages={styleImages}
              setStyleImages={setStyleImages}
              videoReference={videoReference}
              setVideoReference={setVideoReference}
              duration={o1Duration}
              setDuration={setO1Duration}
              aspectRatio={o1AspectRatio}
              setAspectRatio={setO1AspectRatio}
              negativePrompt={o1NegativePrompt}
              setNegativePrompt={setO1NegativePrompt}
              // Action direction & improved prompt
              actionDirection={actionDirection}
              setActionDirection={setActionDirection}
              improvedPrompt={improvedPrompt}
              setImprovedPrompt={setImprovedPrompt}
              // Scene type (action/dialogue)
              sceneType={sceneType}
              setSceneType={setSceneType}
              // Dialogue mode state
              dialoguePrompt={dialoguePrompt}
              setDialoguePrompt={setDialoguePrompt}
              selectedVoiceForDialogue={selectedVoiceForDialogue}
              setSelectedVoiceForDialogue={setSelectedVoiceForDialogue}
              // Scene script (from Compose step)
              sceneScript={scriptSegment}
              // Generation
              generating={generating}
              onGenerateAnimation={handleO1Generate}
              onGenerateDialogue={handleGenerateDialogue}
              // Elapsed timer
              elapsedTime={elapsedTime}
              formatElapsedTime={formatElapsedTime}
              // Character profiles for reference images
              characterProfiles={characterProfiles}
              // Assets
              assets={assets}
              onAssetsChange={fetchAssets}
            />
          )}

          {/* ANIMATE STAGE: Loading or Takes Carousel */}
          {currentStage === "animate" && (
            <AnimateStage
              scene={scene}
              generating={generating}
              animationTakes={animationTakes}
              activeAnimationIndex={activeAnimationIndex}
              onSelectAnimationTake={handleSelectAnimationTake}
            />
          )}

          {/* LIPSYNC STAGE: Apple Motion-style timeline with video and audio tracks */}
          {currentStage === "lipsync" && (
            <LipsyncStage
              scene={scene}
              videoPreviewRef={videoPreviewRef}
              audioPreviewRef={audioPreviewRef}
              uploadedSceneVideo={uploadedSceneVideo}
              animationTakes={animationTakes}
              activeAnimationIndex={activeAnimationIndex}
              applyingLipSync={applyingLipSync}
              selectedVoiceForMerge={selectedVoiceForMerge}
              assets={assets}
              videoDuration={videoDuration}
              setVideoDuration={setVideoDuration}
              audioDuration={audioDuration}
              setAudioDuration={setAudioDuration}
              playheadPosition={playheadPosition}
              setPlayheadPosition={setPlayheadPosition}
              trimStart={trimStart}
              setTrimStart={setTrimStart}
              trimEnd={trimEnd}
              setTrimEnd={setTrimEnd}
              audioOffset={audioOffset}
              setAudioOffset={setAudioOffset}
              isPreviewPlaying={isPreviewPlaying}
              onTogglePreviewPlayback={togglePreviewPlayback}
              onVideoEnded={handleVideoEnded}
              isDraggingTrim={isDraggingTrim}
              setIsDraggingTrim={setIsDraggingTrim}
              syncPreviewUrl={syncPreviewUrl}
              generatingSyncPreview={generatingSyncPreview}
              onGenerateSyncPreview={handleGenerateSyncPreview}
              onClearSyncPreview={handleClearSyncPreview}
            />
          )}

          {/* PRINT STAGE: Final video with print/download options */}
          {currentStage === "print" && (
            <PrintStage
              sceneId={sceneId}
              projectId={projectId}
              scene={scene}
              animationTakes={animationTakes}
              setAnimationTakes={setAnimationTakes}
              lipsyncTakes={lipsyncTakes}
              setLipsyncTakes={setLipsyncTakes}
              activeTakeIndex={activeTakeIndex}
              setActiveTakeIndex={setActiveTakeIndex}
              uploadedSceneVideo={uploadedSceneVideo}
              setUploadedSceneVideo={setUploadedSceneVideo}
              setStrategyChoice={setStrategyChoice}
              setLipsyncUnlockedForUpload={setLipsyncUnlockedForUpload}
              setCurrentStage={setCurrentStage}
              uploadingVideo={uploadingVideo}
              setUploadingVideo={setUploadingVideo}
              printedTakeId={printedTakeId}
              setPrintedTakeId={setPrintedTakeId}
            />
          )}
        </div>

        {/* Right Panel - Stage-specific content (hidden in Preview stage as PreviewStage has its own layout) */}
        {currentStage !== "preview" && currentStage !== "print" && (
        <div className="w-[425px] border-t lg:border-t-0 lg:border-l border-zinc-800 bg-zinc-900/30 flex flex-col min-h-[300px] lg:min-h-0 shrink-0 overflow-y-auto">
            {/* COMPOSE STAGE Content */}
            {/* COMPOSE STAGE Content */}
            {currentStage === "compose" && (
              <>
                {/* Tab Navigation */}
                <div className="flex border-b border-zinc-800 shrink-0">
                  <button
                    onClick={() => setComposeRightTab("scene")}
                    className={`flex-1 px-2 lg:px-4 py-3 text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                      composeRightTab === "scene" 
                        ? "text-indigo-400 border-b-2 border-indigo-400 bg-indigo-500/10" 
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                    }`}
                    data-testid="compose-tab-scene"
                  >
                    <MessageSquare className="w-4 h-4 inline mr-1 lg:mr-2" />
                    Shot
                  </button>
                  <button
                    onClick={() => setComposeRightTab("layers")}
                    className={`flex-1 px-2 lg:px-4 py-3 text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                      composeRightTab === "layers" 
                        ? "text-indigo-400 border-b-2 border-indigo-400 bg-indigo-500/10" 
                        : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                    }`}
                    data-testid="compose-tab-layers"
                  >
                    <Layers className="w-4 h-4 inline mr-1 lg:mr-2" />
                    Layers
                  </button>
                </div>

                {/* Shot Settings Tab Content */}
                {composeRightTab === "scene" && (
                  <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    {/* Copy Setup Section */}
                    <div className="p-3 lg:p-4 border-b border-zinc-800">
                      <h3 className="text-xs lg:text-sm font-medium text-white flex items-center gap-2 mb-2">
                        <FolderInput className="w-4 h-4 text-amber-400" />
                        Copy Setup
                      </h3>
                      <p className="text-[10px] text-zinc-500 mb-3">
                        Copy settings from another shot or duplicate this shot
                      </p>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCopyFromShotDialog(true)}
                          className="w-full h-8 text-xs bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-zinc-300 justify-start"
                          data-testid="copy-from-shot-btn"
                        >
                          <FolderInput className="w-3 h-3 mr-2" />
                          From Another Shot
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Generate default name with letter suffix
                            const currentName = scene?.name || `Shot ${(scene?.order || 0) + 1}`;
                            // Check if name already has a letter suffix like "Shot 3B"
                            const letterMatch = currentName.match(/^(.+?)([A-Z])$/);
                            let newName;
                            if (letterMatch) {
                              // Increment letter: Shot 3B -> Shot 3C
                              const nextLetter = String.fromCharCode(letterMatch[2].charCodeAt(0) + 1);
                              newName = `${letterMatch[1]}${nextLetter}`;
                            } else {
                              // Add first letter suffix: Shot 3 -> Shot 3B
                              newName = `${currentName}B`;
                            }
                            setDuplicateShotName(newName);
                            // Find current scene index in sorted scenes
                            const sortedScenes = [...projectScenes].sort((a, b) => (a.order || 0) - (b.order || 0));
                            const currentIndex = sortedScenes.findIndex(s => s.id === sceneId);
                            setDuplicateInsertIndex(currentIndex + 1); // Default: after current shot
                            setShowDuplicateShotDialog(true);
                          }}
                          className="w-full h-8 text-xs bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-zinc-300 justify-start"
                          data-testid="duplicate-shot-btn"
                        >
                          <Copy className="w-3 h-3 mr-2" />
                          Duplicate This Shot
                        </Button>
                      </div>
                    </div>

                    {/* Script Segment */}
                    <div className="p-3 lg:p-4 border-b border-zinc-800 overflow-hidden">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs lg:text-sm font-medium text-white flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-indigo-400" />
                          Shot Script
                        </h3>
                        {isEditingScript ? (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setIsEditingScript(false); toast.success("Script updated"); }} className="h-6 px-2 text-green-400">
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setScriptSegment(scene?.script_segment || ""); setIsEditingScript(false); }} className="h-6 px-2 text-red-400">
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setIsEditingScript(true)} className="h-6 px-2 text-zinc-400">
                            <Edit3 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      {isEditingScript ? (
                        <textarea
                          value={scriptSegment}
                          onChange={(e) => setScriptSegment(e.target.value)}
                          placeholder="Enter shot script..."
                          className="w-full h-24 lg:h-32 rounded-lg bg-zinc-800/50 border border-indigo-500/50 p-3 text-sm text-zinc-300 font-mono resize-none focus:outline-none"
                          autoFocus
                        />
                      ) : (
                        <div className="bg-zinc-800/50 rounded-lg p-3 max-h-32 overflow-y-auto cursor-pointer hover:bg-zinc-800/70" onClick={() => setIsEditingScript(true)}>
                          <p className="text-sm text-zinc-300 font-mono whitespace-pre-wrap">{scriptSegment || "Click to add script..."}</p>
                        </div>
                      )}
                    </div>

                    {/* Action Direction - Simple text area for user's animation description */}
                    <div className="p-3 lg:p-4 border-b border-zinc-800">
                      <h3 className="text-xs lg:text-sm font-medium text-white flex items-center gap-2 mb-2">
                        <Wand2 className="w-4 h-4 text-purple-400" />
                        Action Direction
                      </h3>
                      <textarea
                        value={actionDirection}
                        onChange={(e) => setActionDirection(e.target.value)}
                        placeholder="Describe what should happen in this shot...&#10;&#10;Example: The character walks forward confidently, stops, and turns to look at the camera with a slight smile."
                        className="w-full h-32 rounded-lg bg-zinc-800/50 border border-zinc-700 p-3 text-zinc-300 text-sm resize-none focus:outline-none focus:border-purple-500"
                        data-testid="action-direction-compose-input"
                      />
                      <p className="text-[10px] text-zinc-500 mt-2">
                        Describe the action in plain language and then anim8pro will optimize it for animation in the next step.
                      </p>
                    </div>

                    {/* Proceed to Preview */}
                    <div className="p-3 lg:p-4">
                      <Button
                        onClick={async () => {
                          setGeneratingPreview(true);
                          await handleSaveScene(true);
                          try {
                            const canvasWidth = canvasRef.current?.offsetWidth || 1920;
                            const canvasHeight = canvasRef.current?.offsetHeight || 1080;
                            
                            // Start preview generation
                            const previewResponse = await axios.post(`${API}/preview`, {
                              scene_id: sceneId,
                              canvas_width: canvasWidth,
                              canvas_height: canvasHeight
                            }, { headers: getAuthHeaders() });
                            
                            // Check if preview completed synchronously (new behavior) or needs polling (legacy)
                            let compositeUrl = null;
                            let pollingFailed = false;
                            
                            if (previewResponse.data.status === 'completed' && previewResponse.data.composite_url) {
                              // Preview completed synchronously - no polling needed
                              compositeUrl = previewResponse.data.composite_url;
                              console.log('Preview completed synchronously:', compositeUrl);
                            } else if (previewResponse.data.job_id) {
                              // Legacy: Poll for job completion to get composite_image_url
                              toast.info('Generating preview image...');
                              const result = await pollPreviewJobCompletion(previewResponse.data.job_id, null);
                              if (result.success && result.composite_url) {
                                compositeUrl = result.composite_url;
                              } else if (!result.success && !result.cancelled) {
                                // Don't throw - just log and try to fetch the scene
                                console.warn('Polling failed, will try to fetch scene directly:', result.error);
                                pollingFailed = true;
                              }
                            }
                            
                            // Wait a bit if polling failed to give the backend time to complete
                            if (pollingFailed) {
                              await new Promise(resolve => setTimeout(resolve, 3000));
                            }
                            
                            // Fetch the updated scene from backend (which has composite_image_url set)
                            const updatedSceneResponse = await axios.get(`${API}/scenes/${sceneId}`, {
                              headers: getAuthHeaders()
                            });
                            const updatedSceneData = updatedSceneResponse.data;
                            
                            // Make sure we have the composite URL either from polling or from scene fetch
                            const finalCompositeUrl = compositeUrl || updatedSceneData?.composite_image_url;
                            
                            // If we still don't have a composite URL after all attempts, show a warning
                            if (!finalCompositeUrl) {
                              toast.warning('Preview image may still be generating. Please wait...');
                            }
                            
                            if (previewResponse.data.prompt) {
                              setFullKlingPrompt(previewResponse.data.prompt);
                            }
                            // Auto-populate O1 elements from placed characters
                            populateO1Elements();
                            
                            // Run Translate Action if there's an action direction
                            if (actionDirection.trim()) {
                              try {
                                const translateResponse = await axios.post(`${API}/o1/improve-prompt`, {
                                  scene_id: sceneId,
                                  user_description: actionDirection,
                                  elements: o1Elements,
                                  item_references: [],
                                  composite_image_url: finalCompositeUrl
                                }, { headers: getAuthHeaders() });
                                
                                if (translateResponse.data?.success) {
                                  const result = translateResponse.data.result;
                                  setImprovedPrompt(result.improved_prompt || "");
                                  // Auto-detect scene type and set appropriate tab
                                  const detectedType = result.scene_type || "action";
                                  setSceneType(detectedType);
                                  // Initialize dialogue prompt with default if dialogue detected
                                  if (detectedType === "dialogue" && !dialoguePrompt) {
                                    setDialoguePrompt(DEFAULT_DIALOGUE_PROMPT);
                                  }
                                }
                              } catch (translateErr) {
                                console.error('Translation failed:', translateErr);
                                // Don't block navigation, just continue
                              }
                            }
                            
                            // Update scene state with composite URL - this will trigger useEffect to navigate
                            setScene({
                              ...updatedSceneData,
                              composite_image_url: finalCompositeUrl || updatedSceneData?.composite_image_url
                            });
                            
                            toast.success('Preview generated!');
                            
                            // Set flag to trigger navigation after state update propagates
                            setPendingPreviewNavigation(true);
                          } catch (e) {
                            console.error('Preview generation failed:', e);
                            toast.error('Failed to generate preview: ' + (e.response?.data?.detail || e.message || 'Unknown error'));
                          } finally {
                            setGeneratingPreview(false);
                          }
                        }}
                        disabled={generatingPreview || (!selectedBackground && placedCharacters.length === 0) || !actionDirection.trim()}
                        className="w-full bg-indigo-500 hover:bg-indigo-600 gap-2"
                        data-testid="proceed-to-preview-btn"
                      >
                        {generatingPreview ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Preparing Preview...</>
                        ) : (
                          <><Eye className="w-4 h-4" /> Preview Shot</>
                        )}
                      </Button>
                      {((!selectedBackground && placedCharacters.length === 0) || !actionDirection.trim()) && (
                        <p className="text-[10px] text-amber-400 mt-2 text-center">
                          {!actionDirection.trim() 
                            ? "Enter an action direction above"
                            : "Add at least one asset (background, character, or item)"
                          }
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Layer Settings Tab Content */}
                {composeRightTab === "layers" && (
                  <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {/* Grid Controls - At top */}
                    <div className="p-3 border-b border-zinc-800 shrink-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowGrid(!showGrid)}
                          className={`h-8 px-3 ${showGrid ? 'text-indigo-400 bg-indigo-500/20' : 'text-zinc-400 hover:text-white'}`}
                          data-testid="toggle-grid-btn"
                        >
                          <Grid className="w-4 h-4 mr-2" />
                          {showGrid ? 'Hide' : 'Show'} Grid
                        </Button>
                        
                        {showGrid && (
                          <>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-zinc-500">Size:</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setGridSize(Math.max(2, gridSize - 1))}
                                disabled={gridSize <= 2}
                                className="w-6 h-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                              >
                                <span className="text-sm">−</span>
                              </Button>
                              <span className="text-xs text-zinc-300 min-w-[35px] text-center">{gridSize}×{gridSize}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setGridSize(Math.min(20, gridSize + 1))}
                                disabled={gridSize >= 20}
                                className="w-6 h-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                              >
                                <span className="text-sm">+</span>
                              </Button>
                            </div>
                            
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-zinc-500">Line:</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setGridLineWidth(Math.max(1, gridLineWidth - 1))}
                                disabled={gridLineWidth <= 1}
                                className="w-6 h-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                              >
                                <span className="text-sm">−</span>
                              </Button>
                              <span className="text-xs text-zinc-300 min-w-[28px] text-center">{gridLineWidth}px</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setGridLineWidth(Math.min(5, gridLineWidth + 1))}
                                disabled={gridLineWidth >= 5}
                                className="w-6 h-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                              >
                                <span className="text-sm">+</span>
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Layers List */}
                    <div className="flex-1 min-h-0 flex flex-col">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 shrink-0">
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 text-indigo-400" />
                          <span className="text-sm font-medium text-white">Layers</span>
                          <span className="text-xs text-zinc-500">({getActiveCharacters().length})</span>
                          {editingEndFrame && hasEndFrame && (
                            <span className="text-xs bg-orange-600/30 text-orange-300 px-2 py-0.5 rounded">End Frame</span>
                          )}
                        </div>
                        <span className="text-xs text-zinc-500">Front → Back</span>
                      </div>
                      
                      <ScrollArea className="flex-1 min-h-0">
                        <div className="p-2 space-y-1">
                          {getActiveCharacters().length === 0 && audioTracks.length === 0 ? (
                            <div className="flex items-center justify-center text-zinc-500 text-sm py-8">
                              No layers - add characters or props
                            </div>
                          ) : (
                            <>
                              {getActiveCharacters().map((char, index) => (
                                <LayerItem 
                                  key={char.asset_id + '-layer-tab-' + index}
                                  char={char}
                                  index={index}
                                  isSelected={selectedCharacter === index}
                                  isModal={true}
                                  activeCharactersLength={getActiveCharacters().length}
                                  editingLayerName={editingLayerName}
                                  setEditingLayerName={setEditingLayerName}
                                  editingProperty={editingProperty}
                                  setEditingProperty={setEditingProperty}
                                  onSelect={setSelectedCharacter}
                                  onNameChange={handleCharacterNameChange}
                                  onVisibilityToggle={handleCharacterVisibility}
                                  onMoveUp={handleLayerMoveUp}
                                  onMoveDown={handleLayerMoveDown}
                                  onRemove={handleCharacterRemove}
                                  onFlip={handleCharacterFlip}
                                  onPropertyDragStart={handlePropertyDragStart}
                                  onPropertyChange={handlePropertyChangeFromDraggable}
                                />
                              ))}
                              
                              {audioTracks.length > 0 && (
                                <>
                                  <div className="h-px bg-zinc-700 my-3" />
                                  <h4 className="text-xs text-zinc-500 mb-2 px-1">Audio Tracks</h4>
                                  {audioTracks.map((track, index) => (
                                    <div
                                      key={track.asset_id + '-audio-tab-' + index}
                                      className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30"
                                    >
                                      <div className="w-6 h-6 rounded bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                                        <Mic className="w-3 h-3 text-amber-400" />
                                      </div>
                                      <span className="text-xs flex-1 truncate text-amber-200">{track.name}</span>
                                      <Button variant="ghost" size="icon" onClick={() => handleAudioRemove(index)} className="w-6 h-6 p-0 text-zinc-400 hover:text-red-400">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                    
                    {/* Background Settings */}
                    {selectedBackground && (
                      <div className="px-3 py-3 border-t border-zinc-800 bg-zinc-800/30 shrink-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Image className="w-4 h-4 text-indigo-400" />
                          <span className="text-xs text-zinc-400 font-medium">Background Settings</span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-zinc-500">Zoom:</span>
                            <Button variant="ghost" size="icon" onClick={() => setBackgroundZoom(Math.max(50, backgroundZoom - 10))} className="w-6 h-6 p-0 text-zinc-400 hover:text-white">
                              <ZoomOut className="w-3 h-3" />
                            </Button>
                            <span className="text-xs text-zinc-300 w-10 text-center">{backgroundZoom}%</span>
                            <Button variant="ghost" size="icon" onClick={() => setBackgroundZoom(Math.min(200, backgroundZoom + 10))} className="w-6 h-6 p-0 text-zinc-400 hover:text-white">
                              <ZoomIn className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-zinc-500">Blur:</span>
                            <Button variant="ghost" size="icon" onClick={() => setBackgroundBlur(Math.max(0, backgroundBlur - 1))} className="w-5 h-5 p-0 text-zinc-400 hover:text-white text-xs">−</Button>
                            <span className="text-xs text-zinc-300 w-8 text-center">{backgroundBlur}px</span>
                            <Button variant="ghost" size="icon" onClick={() => setBackgroundBlur(Math.min(20, backgroundBlur + 1))} className="w-5 h-5 p-0 text-zinc-400 hover:text-white text-xs">+</Button>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => { setBackgroundZoom(100); setBackgroundPan({x:0, y:0}); setBackgroundBlur(0); }} 
                            className="h-6 px-2 text-zinc-400 hover:text-white text-xs"
                          >
                            <Target className="w-3 h-3 mr-1" />
                            Reset
                          </Button>
                        </div>
                        <p className="text-[10px] text-zinc-600 mt-2">Drag canvas to pan background</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* PREVIEW STAGE Content - Handled by PreviewStage component in main area */}
            {currentStage === "preview" && (
              <div className="p-4 text-center">
                <p className="text-xs text-zinc-500">
                  Preview controls are in the main panel →
                </p>
              </div>
            )}

            {/* ANIMATE STAGE Content */}
            {currentStage === "animate" && (
              <>
                {/* Info Note about mouth movements */}
                <div className="p-3 mx-4 mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-300 flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">💡</span>
                    <span>
                      <strong>Note:</strong> If your characters are talking, Mouth movements will not match audio yet. Focus on the overall animation quality and mouth shapes — lip sync is applied in the next step.
                    </span>
                  </p>
                </div>

                {/* Use This Take Button - Primary CTA */}
                {animationTakes.length > 0 && !generating && scene?.status !== "generating" && (
                  <div className="p-4 border-b border-zinc-800 bg-indigo-500/10">
                    <Button
                      onClick={() => handleChooseTake(activeAnimationIndex)}
                      className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Use Take {activeAnimationIndex + 1} for Lip Sync
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Skip Lip Sync Option */}
                {animationTakes.length > 0 && !generating && scene?.status !== "generating" && (
                  <div className="p-4 border-b border-zinc-800">
                    <h3 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                      <FastForward className="w-4 h-4 text-orange-400" />
                      Skip Lip Sync
                    </h3>
                    <p className="text-xs text-zinc-500 mb-3">
                      For scenes without dialog, skip directly to the Print step.
                    </p>
                    <Button
                      onClick={handleSkipLipsync}
                      variant="outline"
                      className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/20 gap-2"
                    >
                      <FastForward className="w-4 h-4" /> Skip Lip Sync
                      {skipLipsync && <Badge variant="outline" className="ml-2 text-[10px]">Saved</Badge>}
                    </Button>
                    {skipLipsync && (
                      <p className="text-[10px] text-orange-400 mt-2">
                        This scene is set to skip lip sync. You can also go directly to Print.
                      </p>
                    )}
                  </div>
                )}

                {/* Generate New Take - Goes back to Preview */}
                <div className="p-4 border-b border-zinc-800">
                  <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <Wand2 className="w-4 h-4 text-indigo-400" />
                    Create Another Take
                  </h3>
                  <p className="text-xs text-zinc-500 mb-3">
                    Go back to Preview to modify your action and create a new animation take.
                  </p>
                  <Button
                    onClick={() => setCurrentStage("preview")}
                    className="w-full bg-indigo-500 hover:bg-indigo-600 gap-2"
                  >
                    <Wand2 className="w-4 h-4" /> Let&apos;s Go Again
                  </Button>
                </div>

                {/* Takes Info */}
                {animationTakes.length > 0 && (
                  <div className="p-4 border-b border-zinc-800">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">Takes Available:</span>
                      <span className="text-white font-medium">{animationTakes.length}</span>
                    </div>
                  </div>
                )}

                {/* Delete Takes */}
                {animationTakes.length > 1 && (
                  <div className="p-4 border-b border-zinc-800">
                    <Button
                      onClick={() => handleDeleteAnimationTake(activeAnimationIndex)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete Current Take
                    </Button>
                  </div>
                )}

                {/* Back to Preview */}
                <div className="p-4">
                  <Button
                    onClick={() => setCurrentStage("preview")}
                    variant="ghost"
                    className="w-full text-zinc-400 hover:text-white gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to Preview
                  </Button>
                </div>
              </>
            )}

            {/* LIPSYNC STAGE Content */}
            {currentStage === "lipsync" && (
              <>
                {/* Tips Note for lip sync */}
                <div className="p-3 mx-4 mt-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-xs text-purple-300 flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">💡</span>
                    <span>
                      <strong>Tips:</strong> Drag the audio waveform to roughly align it with the animation in the video. 
                      Trim the beginning and end of the video to match your audio clip for best results. 
                      If the dialogue is longer than the clip, the animation will be extended to match the full audio.
                    </span>
                  </p>
                </div>

                {/* Voice Selection */}
                <div className="p-4 border-b border-zinc-800">
                  <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <Mic className="w-4 h-4 text-purple-400" />
                    Apply Lip Sync
                  </h3>
                  
                  {/* Voice File Options */}
                  <div className="space-y-3 mb-4">
                    {/* Select existing voice */}
                    <Select value={selectedVoiceForMerge || ""} onValueChange={setSelectedVoiceForMerge}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Select voice file" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {/* Actor Recordings Section */}
                        {(scene?.audio_tracks || []).length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-medium text-purple-400 border-b border-zinc-700">
                              Actor Recordings
                            </div>
                            {(scene?.audio_tracks || []).map(track => {
                              const trackName = track.name || `${track.character_name} - Recording`;
                              return (
                                <SelectItem key={track.id} value={track.id} className="text-white hover:bg-zinc-700">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
                                    <span className="flex-1 truncate" title={trackName}>
                                      {trackName.length > 35 ? trackName.substring(0, 35) + "..." : trackName}
                                    </span>
                                    {track.is_favorite && (
                                      <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />
                                    )}
                                    {track.note && (
                                      <MessageSquare className="w-3 h-3 text-blue-400 shrink-0" />
                                    )}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </>
                        )}
                        {/* Library Voice Assets Section */}
                        {assets.filter(a => a.type === 'voice').length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-medium text-zinc-400 border-b border-zinc-700">
                              Voice Library
                            </div>
                            {assets.filter(a => a.type === 'voice').map(voice => (
                              <SelectItem key={voice.id} value={voice.id} className="text-white hover:bg-zinc-700">
                                <span className="truncate" title={voice.name}>
                                  {voice.name.length > 35 ? voice.name.substring(0, 35) + "..." : voice.name}
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {(scene?.audio_tracks || []).length === 0 && assets.filter(a => a.type === 'voice').length === 0 && (
                          <div className="px-2 py-3 text-center text-zinc-500 text-sm">
                            No voice files available
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    
                    {/* Show actor note if selected voice has one */}
                    {(() => {
                      const selectedTrack = (scene?.audio_tracks || []).find(t => t.id === selectedVoiceForMerge);
                      if (selectedTrack?.note) {
                        return (
                          <div className="p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <div className="flex items-center gap-1 text-blue-400 text-xs font-medium mb-1">
                              <MessageSquare className="w-3 h-3" />
                              Actor Note
                            </div>
                            <p className="text-sm text-zinc-300">{selectedTrack.note}</p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Or divider */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-zinc-700" />
                      <span className="text-xs text-zinc-500">OR</span>
                      <div className="flex-1 h-px bg-zinc-700" />
                    </div>
                    
                    {/* Record Voice */}
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Mic className="w-4 h-4 text-purple-400" />
                        <span className="text-xs text-zinc-300 font-medium">Record Voice</span>
                      </div>
                      <VoiceRecorder
                        compact
                        showUploadOption={false}
                        onRecordingComplete={async (blob) => {
                          try {
                            toast.info("Uploading recording...");
                            const asset = await uploadBlobToS3(blob, projectId, `voice-recording-${Date.now()}.webm`, {
                              type: 'voice',
                              name: `Voice Recording ${new Date().toLocaleTimeString()}`
                            });
                            setAssets(prev => [...prev, asset]);
                            setSelectedVoiceForMerge(asset.id);
                            toast.success("Recording uploaded!");
                          } catch (err) {
                            toast.error("Failed to upload recording");
                            console.error(err);
                          }
                        }}
                      />
                    </div>
                    
                    {/* Upload / Import buttons */}
                    <div className="flex gap-2">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              toast.info("Uploading voice file...");
                              const asset = await uploadFileToS3(file, projectId, {
                                type: 'voice',
                                name: file.name || "uploaded_voice"
                              });
                              setAssets(prev => [...prev, asset]);
                              setSelectedVoiceForMerge(asset.id);
                              toast.success("Voice file uploaded!");
                            } catch (err) {
                              toast.error("Failed to upload voice file");
                              console.error(err);
                            }
                          }}
                          data-testid="upload-voice-input"
                        />
                        <Button variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 gap-2" asChild>
                          <span>
                            <Upload className="w-4 h-4" />
                            Upload Voice
                          </span>
                        </Button>
                      </label>
                      
                      <Button 
                        variant="outline" 
                        className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 gap-2"
                        onClick={() => setShowVoiceImportDialog(true)}
                        data-testid="import-voice-btn"
                      >
                        <FolderInput className="w-4 h-4" />
                        Import Voice
                      </Button>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleApplyLipSync}
                    disabled={applyingLipSync || !selectedVoiceForMerge}
                    className="w-full bg-purple-500 hover:bg-purple-600 gap-2"
                  >
                    {applyingLipSync ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Processing... {formatElapsedTime(elapsedTime)}</>
                    ) : (
                      <><Mic className="w-4 h-4" /> Apply Lip Sync</>
                    )}
                  </Button>
                </div>

                {/* Existing Takes Info */}
                {lipsyncTakes.length > 0 && (
                  <div className="p-4 border-b border-zinc-800">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">Existing Lip Sync Takes:</span>
                      <span className="text-white font-medium">{lipsyncTakes.length}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">
                      Generate additional takes with different timing or voice files
                    </p>
                  </div>
                )}

                {/* Back to Animate */}
                <div className="p-4">
                  <Button
                    onClick={() => setCurrentStage("animate")}
                    variant="ghost"
                    className="w-full text-zinc-400 hover:text-white gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to Animate
                  </Button>
                </div>
              </>
            )}

            {/* PRINT STAGE - Right panel handled by PrintStage component */}
            {currentStage === "print" && (
              <div className="p-4 text-center text-zinc-500 text-sm">
                Use the panel on the right to print or download your video.
              </div>
            )}
        </div>
        )}
      </div>

      {/* Fullscreen Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-6xl w-[95vw] max-h-[95vh] overflow-hidden p-0">
          <div className="flex flex-col h-full max-h-[95vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
              <DialogTitle className="text-white flex items-center gap-2 text-lg">
                <Film className="w-5 h-5 text-indigo-400" />
                Animation Video
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowVideoModal(false)}
                className="w-8 h-8 p-0 text-zinc-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Video Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Video Area */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Show lip sync result if available, otherwise generated video */}
                  {lipSyncVideoUrl ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-green-400">Lip Sync Result</span>
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Enhanced</span>
                      </div>
                      <video 
                        src={lipSyncVideoUrl} 
                        controls 
                        autoPlay
                        className="w-full rounded-lg bg-black aspect-video"
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <a href={lipSyncVideoUrl} download className="text-xs text-indigo-400 hover:underline flex items-center gap-1">
                          <Download className="w-3 h-3" /> Download Lip Sync Video
                        </a>
                      </div>
                    </div>
                  ) : mergedVideoUrl ? (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-amber-400">Merged Audio Video</span>
                      </div>
                      <video 
                        src={mergedVideoUrl} 
                        controls 
                        autoPlay
                        className="w-full rounded-lg bg-black aspect-video"
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <a href={mergedVideoUrl} download className="text-xs text-indigo-400 hover:underline flex items-center gap-1">
                          <Download className="w-3 h-3" /> Download Merged Video
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-white">Generated Animation</span>
                      </div>
                      <video 
                        src={getAssetUrl(scene?.generated_video_url)} 
                        controls 
                        autoPlay
                        className="w-full rounded-lg bg-black aspect-video"
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <a href={getAssetUrl(scene?.generated_video_url)} download className="text-xs text-indigo-400 hover:underline flex items-center gap-1">
                          <Download className="w-3 h-3" /> Download Original
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Original video (if we have processed versions) */}
                  {(lipSyncVideoUrl || mergedVideoUrl) && scene?.generated_video_url && (
                    <div className="pt-4 border-t border-zinc-800">
                      <details className="group">
                        <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-400 flex items-center gap-1">
                          <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
                          View Original Animation
                        </summary>
                        <div className="mt-3">
                          <video 
                            src={getAssetUrl(scene.generated_video_url)} 
                            controls 
                            className="w-full rounded-lg bg-black aspect-video"
                          />
                        </div>
                      </details>
                    </div>
                  )}
                </div>
                
                {/* Lip Sync Controls */}
                <div className="space-y-4">
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                      <Mic className="w-4 h-4 text-purple-400" />
                      Lip Sync
                    </h3>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-zinc-400 mb-1.5 block">Select Voice File</Label>
                        <Select value={selectedVoiceForMerge || ""} onValueChange={setSelectedVoiceForMerge}>
                          <SelectTrigger className="h-9 text-sm bg-zinc-900 border-zinc-700">
                            <SelectValue placeholder="Choose voice file..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getAssetsByType("voice").map((v) => (
                              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Button 
                        onClick={handleApplyLipSync} 
                        disabled={applyingLipSync || !selectedVoiceForMerge} 
                        className="w-full bg-purple-500 hover:bg-purple-600"
                      >
                        {applyingLipSync ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing Lip Sync...</>
                        ) : (
                          <><Wand2 className="w-4 h-4 mr-2" /> Apply Lip Sync</>
                        )}
                      </Button>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-zinc-700" />
                        </div>
                        <div className="relative flex justify-center">
                          <span className="px-2 bg-zinc-800/50 text-xs text-zinc-500">or</span>
                        </div>
                      </div>
                      
                      <Button 
                        onClick={handleMergeAudio} 
                        disabled={merging || !selectedVoiceForMerge} 
                        variant="outline" 
                        className="w-full border-zinc-700"
                      >
                        {merging ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Merging...</>
                        ) : (
                          <><Volume2 className="w-4 h-4 mr-2" /> Simple Audio Merge</>
                        )}
                      </Button>
                    </div>
                    
                    {/* Info */}
                    <div className="mt-4 p-3 bg-zinc-900/50 rounded-lg">
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        <strong className="text-purple-400">Lip Sync</strong> matches mouth movements to the voice audio.
                        <br /><br />
                        <strong className="text-zinc-400">Simple Merge</strong> just overlays the audio without lip sync.
                      </p>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-white mb-3">Quick Actions</h3>
                    <div className="space-y-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setShowVideoModal(false);
                          handleGenerate();
                        }}
                        className="w-full border-zinc-700 justify-start"
                      >
                        <RotateCw className="w-3 h-3 mr-2" />
                        Regenerate Animation
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-blue-500" />
              Import {importAssetType}s
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Source Project</Label>
              {loadingProjects ? (
                <div className="flex items-center gap-2 text-zinc-500 p-3">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                </div>
              ) : (
                <Select value={selectedImportProject} onValueChange={handleProjectSelect}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Choose a project..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {importableProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selectedImportProject && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Select Assets</Label>
                {loadingAssets ? (
                  <div className="flex items-center gap-2 text-zinc-500 p-3">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                  </div>
                ) : importableAssets.length === 0 ? (
                  <p className="text-zinc-500 text-sm p-3">No {importAssetType}s in this project</p>
                ) : (
                  <ScrollArea className="h-[200px] border border-zinc-800 rounded-lg p-2">
                    <div className="space-y-1">
                      {importableAssets.map((asset) => (
                        <div
                          key={asset.id}
                          className="flex items-center gap-3 p-2 hover:bg-zinc-800/50 rounded cursor-pointer"
                          onClick={() => {
                            setSelectedImportAssets(prev => 
                              prev.includes(asset.id) ? prev.filter(id => id !== asset.id) : [...prev, asset.id]
                            );
                          }}
                        >
                          <Checkbox checked={selectedImportAssets.includes(asset.id)} className="border-zinc-600" />
                          <span className="text-white text-sm truncate">{asset.name}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowImportDialog(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleImportAssets} disabled={importing || selectedImportAssets.length === 0} className="bg-blue-500 hover:bg-blue-600">
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FolderInput className="w-4 h-4 mr-1" />}
              Import ({selectedImportAssets.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Import Dialog for Lip Sync */}
      <Dialog 
        open={showVoiceImportDialog} 
        onOpenChange={(open) => {
          setShowVoiceImportDialog(open);
          if (open) {
            fetchVoiceImportProjects();
          } else {
            setSelectedVoiceImportProject(null);
            setVoiceImportAssets([]);
          }
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-purple-500" />
              Import Voice from Another Project
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Source Project</Label>
              {loadingVoiceImport && !selectedVoiceImportProject ? (
                <div className="flex items-center gap-2 text-zinc-500 p-3">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading projects...
                </div>
              ) : (
                <Select 
                  value={selectedVoiceImportProject || ""} 
                  onValueChange={(val) => {
                    setSelectedVoiceImportProject(val);
                    setVoiceImportAssets([]);
                    if (val) fetchVoiceImportAssets(val);
                  }}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Choose a project..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {voiceImportProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>
                    ))}
                    {voiceImportProjects.length === 0 && (
                      <div className="px-2 py-3 text-center text-zinc-500 text-sm">
                        No other projects found
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selectedVoiceImportProject && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Select Voice File</Label>
                {loadingVoiceImport ? (
                  <div className="flex items-center gap-2 text-zinc-500 p-3">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading voice files...
                  </div>
                ) : voiceImportAssets.length === 0 ? (
                  <p className="text-zinc-500 text-sm p-3">No voice files in this project</p>
                ) : (
                  <ScrollArea className="h-[200px] border border-zinc-800 rounded-lg p-2">
                    <div className="space-y-1">
                      {voiceImportAssets.map((asset) => (
                        <button
                          key={asset.id}
                          className="w-full flex items-center gap-3 p-3 hover:bg-zinc-800/50 rounded cursor-pointer text-left transition-colors"
                          onClick={() => handleImportVoice(asset)}
                          data-testid={`voice-import-${asset.id}`}
                        >
                          <div className="w-10 h-10 rounded bg-purple-500/20 flex items-center justify-center">
                            <Mic className="w-5 h-5 text-purple-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-white text-sm block truncate">{asset.name}</span>
                            <span className="text-zinc-500 text-xs">Click to import</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-500" />
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" onClick={() => setShowVoiceImportDialog(false)} className="text-zinc-400">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Layers Panel - Positioned over right panel without darkening */}
      {showLayersModal && (
        <div className="fixed top-16 right-0 w-[25%] min-w-[350px] h-[calc(100vh-64px)] bg-zinc-900 border-l border-zinc-800 shadow-2xl z-40 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 shrink-0">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-400" />
              <span className="text-base font-medium text-white">Layers ({getActiveCharacters().length})</span>
              {editingEndFrame && hasEndFrame && (
                <span className="text-xs bg-orange-600/30 text-orange-300 px-2 py-0.5 rounded">End Frame</span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowLayersModal(false)}
              className="w-8 h-8 p-0 text-zinc-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <ScrollArea className="flex-1 min-h-0 p-3">
            <div className="space-y-2">
              {getActiveCharacters().length === 0 ? (
                <div className="text-center py-8 text-zinc-500">No layers yet</div>
              ) : (
                getActiveCharacters().map((char, index) => (
                  <LayerItem 
                    key={char.asset_id + '-modal-' + index}
                    char={char}
                    index={index}
                    isSelected={selectedCharacter === index}
                    isModal={true}
                    activeCharactersLength={getActiveCharacters().length}
                    editingLayerName={editingLayerName}
                    setEditingLayerName={setEditingLayerName}
                    editingProperty={editingProperty}
                    setEditingProperty={setEditingProperty}
                    onSelect={setSelectedCharacter}
                    onNameChange={handleCharacterNameChange}
                    onVisibilityToggle={handleCharacterVisibility}
                    onMoveUp={handleLayerMoveUp}
                    onMoveDown={handleLayerMoveDown}
                    onRemove={handleCharacterRemove}
                    onFlip={handleCharacterFlip}
                    onPropertyDragStart={handlePropertyDragStart}
                    onPropertyChange={handlePropertyChangeFromDraggable}
                  />
                ))
              )}
              
              {audioTracks.length > 0 && (
                <>
                  <div className="h-px bg-zinc-700 my-4" />
                  <h4 className="text-xs text-zinc-500 mb-2">Audio Tracks</h4>
                  {audioTracks.map((track, index) => (
                    <div key={track.asset_id + '-audio-modal-' + index} className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <Mic className="w-4 h-4 text-amber-400" />
                      <span className="text-sm flex-1 text-amber-200">{track.name}</span>
                      <Button variant="ghost" size="icon" onClick={() => handleAudioRemove(index)} className="w-6 h-6 p-0 text-zinc-400 hover:text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </ScrollArea>
          
          {/* Background Settings - Inside Expanded Layers Panel */}
          {selectedBackground && (
            <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-800/30 flex items-center gap-3 shrink-0 flex-wrap">
              <Image className="w-4 h-4 text-indigo-400" />
              <span className="text-xs text-zinc-400">Background:</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => setBackgroundZoom(Math.max(50, backgroundZoom - 10))} className="w-7 h-7 p-0 text-zinc-400 hover:text-white">
                  <ZoomOut className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs text-zinc-300 w-10 text-center font-medium">{backgroundZoom}%</span>
                <Button variant="ghost" size="icon" onClick={() => setBackgroundZoom(Math.min(200, backgroundZoom + 10))} className="w-7 h-7 p-0 text-zinc-400 hover:text-white">
                  <ZoomIn className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="h-5 w-px bg-zinc-700" />
              <div className="flex items-center gap-1">
                <span className="text-xs text-zinc-400">Blur:</span>
                <Button variant="ghost" size="icon" onClick={() => setBackgroundBlur(Math.max(0, backgroundBlur - 1))} className="w-6 h-6 p-0 text-zinc-400 hover:text-white text-sm">
                  −
                </Button>
                <span className="text-xs text-zinc-300 w-8 text-center font-medium">{backgroundBlur}px</span>
                <Button variant="ghost" size="icon" onClick={() => setBackgroundBlur(Math.min(20, backgroundBlur + 1))} className="w-6 h-6 p-0 text-zinc-400 hover:text-white text-sm">
                  +
                </Button>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setBackgroundZoom(100); setBackgroundPan({ x: 0, y: 0 }); setBackgroundBlur(0); }} 
                className="h-7 px-2 text-xs text-zinc-400 hover:text-white"
                title="Reset zoom, pan & blur"
              >
                <Target className="w-3 h-3 mr-1" />
                Reset
              </Button>
              <span className="text-[10px] text-zinc-600 ml-auto">Drag canvas to pan</span>
            </div>
          )}
        </div>
      )}

      {/* Copy from Another Shot Dialog */}
      <Dialog 
        open={showCopyFromShotDialog} 
        onOpenChange={setShowCopyFromShotDialog}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-amber-500" />
              Copy from Another Shot
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Copy the background and character placements from another shot in this project.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Search shots by name..."
                value={copySourceSearch}
                onChange={(e) => setCopySourceSearch(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            
            {/* Scene List */}
            <ScrollArea className="h-[300px] border border-zinc-800 rounded-lg">
              <div className="p-2 space-y-1">
                {projectScenes
                  .filter(s => s.id !== sceneId) // Exclude current scene
                  .filter(s => !copySourceSearch || 
                    s.name?.toLowerCase().includes(copySourceSearch.toLowerCase()) ||
                    s.script_segment?.toLowerCase().includes(copySourceSearch.toLowerCase())
                  )
                  .map((s) => {
                    // Truncate shot name to ~40 chars
                    const shotName = s.name || `Shot ${(s.order || 0) + 1}`;
                    const displayName = shotName.length > 45 ? shotName.substring(0, 45) + '...' : shotName;
                    
                    return (
                    <button
                      key={s.id}
                      onClick={async () => {
                        try {
                          setCopyingFromShot(true);
                          const response = await axios.post(
                            `${API}/scenes/${sceneId}/copy-from/${s.id}`,
                            {},
                            { headers: getAuthHeaders() }
                          );
                          if (response.data.scene) {
                            const copiedScene = response.data.scene;
                            setScene(copiedScene);
                            // Update local state from copied scene
                            setSelectedBackground(copiedScene.background_id || null);
                            setPlacedCharacters(copiedScene.characters || []);
                            // Also copy background zoom, pan, and blur settings
                            setBackgroundZoom(copiedScene.background_zoom ?? 100);
                            setBackgroundPan(copiedScene.background_pan || { x: 0, y: 0 });
                            setBackgroundBlur(copiedScene.background_blur ?? 0);
                          }
                          toast.success(`Copied from "${displayName}"`);
                          setShowCopyFromShotDialog(false);
                          setCopySourceSearch("");
                        } catch (err) {
                          console.error('Copy failed:', err);
                          toast.error('Failed to copy: ' + (err.response?.data?.detail || err.message));
                        } finally {
                          setCopyingFromShot(false);
                        }
                      }}
                      disabled={copyingFromShot}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 border border-transparent hover:border-amber-500/50 transition-all text-left group"
                    >
                      {/* Thumbnail */}
                      <div className="w-20 h-12 rounded bg-zinc-700 overflow-hidden shrink-0">
                        {s.composite_image_url || s.thumbnail_url ? (
                          <img 
                            src={s.composite_image_url || s.thumbnail_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-4 h-4 text-zinc-600" />
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate" title={shotName}>
                          {displayName}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {s.characters?.length || 0} characters • {s.background_id ? 'Has BG' : 'No BG'}
                        </div>
                      </div>
                      {/* Arrow */}
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-amber-500 transition-colors shrink-0" />
                    </button>
                    );
                  })}
                {projectScenes.filter(s => s.id !== sceneId).length === 0 && (
                  <div className="text-center text-zinc-500 text-sm py-8">
                    No other shots in this project
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="ghost" 
              onClick={() => { setShowCopyFromShotDialog(false); setCopySourceSearch(""); }} 
              className="text-zinc-400"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Shot Dialog */}
      <Dialog 
        open={showDuplicateShotDialog} 
        onOpenChange={(open) => {
          setShowDuplicateShotDialog(open);
          if (!open) {
            setDuplicateShotName("");
            setDuplicateInsertIndex(null);
          }
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Copy className="w-5 h-5 text-amber-500" />
              Duplicate This Shot
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Create a copy of this shot with the same setup (background, characters, script).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Shot Name Input */}
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm">Shot Name</Label>
              <Input
                value={duplicateShotName}
                onChange={(e) => setDuplicateShotName(e.target.value)}
                placeholder="Enter shot name..."
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="duplicate-shot-name-input"
              />
            </div>
            
            {/* Insertion Position */}
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm">Where do you want to insert this shot?</Label>
              <ScrollArea className="h-[250px] border border-zinc-800 rounded-lg">
                <div className="p-2">
                  {(() => {
                    const sortedScenes = [...projectScenes].sort((a, b) => (a.order || 0) - (b.order || 0));
                    const items = [];
                    
                    // Add "Before first shot" option
                    items.push(
                      <button
                        key="before-first"
                        onClick={() => setDuplicateInsertIndex(0)}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                          duplicateInsertIndex === 0
                            ? 'bg-amber-500/20 border border-amber-500/50'
                            : 'bg-zinc-800/30 hover:bg-zinc-800/50 border border-transparent'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full border-2 ${
                          duplicateInsertIndex === 0 ? 'border-amber-500 bg-amber-500' : 'border-zinc-600'
                        }`} />
                        <span className="text-sm text-zinc-400">Before first shot</span>
                      </button>
                    );
                    
                    // Add each shot with "After Shot X" option
                    sortedScenes.forEach((s, idx) => {
                      const shotName = s.name || `Shot ${idx + 1}`;
                      const displayName = shotName.length > 35 ? shotName.substring(0, 35) + '...' : shotName;
                      const isCurrentShot = s.id === sceneId;
                      
                      // Shot display (not clickable, just for context)
                      items.push(
                        <div
                          key={`shot-${s.id}`}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg ${
                            isCurrentShot ? 'bg-indigo-500/10 border border-indigo-500/30' : 'bg-zinc-800/20'
                          }`}
                        >
                          <div className="w-12 h-8 rounded bg-zinc-700 overflow-hidden shrink-0">
                            {s.composite_image_url || s.thumbnail_url ? (
                              <img 
                                src={s.composite_image_url || s.thumbnail_url} 
                                alt="" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image className="w-3 h-3 text-zinc-600" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${isCurrentShot ? 'text-indigo-300' : 'text-white'}`}>
                              {displayName}
                              {isCurrentShot && <span className="text-xs text-indigo-400 ml-2">(current)</span>}
                            </div>
                          </div>
                        </div>
                      );
                      
                      // "After this shot" insertion point
                      items.push(
                        <button
                          key={`after-${s.id}`}
                          onClick={() => setDuplicateInsertIndex(idx + 1)}
                          className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all ${
                            duplicateInsertIndex === idx + 1
                              ? 'bg-amber-500/20 border border-amber-500/50'
                              : 'bg-zinc-800/30 hover:bg-zinc-800/50 border border-transparent'
                          }`}
                        >
                          <div className={`w-3 h-3 rounded-full border-2 ${
                            duplicateInsertIndex === idx + 1 ? 'border-amber-500 bg-amber-500' : 'border-zinc-600'
                          }`} />
                          <span className="text-sm text-zinc-400">
                            {idx === sortedScenes.length - 1 ? 'After last shot' : `After ${displayName.substring(0, 20)}${displayName.length > 20 ? '...' : ''}`}
                          </span>
                        </button>
                      );
                    });
                    
                    return items;
                  })()}
                </div>
              </ScrollArea>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="ghost" 
              onClick={() => setShowDuplicateShotDialog(false)} 
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!duplicateShotName.trim()) {
                  toast.error("Please enter a shot name");
                  return;
                }
                if (duplicateInsertIndex === null) {
                  toast.error("Please select where to insert the shot");
                  return;
                }
                
                try {
                  setDuplicatingShot(true);
                  
                  // Calculate the order value based on insertion index
                  const sortedScenes = [...projectScenes].sort((a, b) => (a.order || 0) - (b.order || 0));
                  let insertOrder;
                  
                  if (duplicateInsertIndex === 0) {
                    // Before first shot
                    const firstOrder = sortedScenes[0]?.order || 1;
                    insertOrder = firstOrder - 0.5;
                  } else if (duplicateInsertIndex >= sortedScenes.length) {
                    // After last shot
                    const lastOrder = sortedScenes[sortedScenes.length - 1]?.order || sortedScenes.length;
                    insertOrder = lastOrder + 1;
                  } else {
                    // Between two shots
                    const prevOrder = sortedScenes[duplicateInsertIndex - 1]?.order || duplicateInsertIndex;
                    const nextOrder = sortedScenes[duplicateInsertIndex]?.order || duplicateInsertIndex + 1;
                    insertOrder = (prevOrder + nextOrder) / 2;
                  }
                  
                  const response = await axios.post(
                    `${API}/scenes/${sceneId}/duplicate`,
                    { 
                      new_name: duplicateShotName.trim(),
                      insert_order: insertOrder 
                    },
                    { headers: getAuthHeaders() }
                  );
                  
                  if (response.data.success && response.data.scene) {
                    const newScene = response.data.scene;
                    
                    // Add to project scenes
                    setProjectScenes(prev => [...prev, newScene].sort((a, b) => (a.order || 0) - (b.order || 0)));
                    
                    toast.success(`Created "${duplicateShotName}"`);
                    setShowDuplicateShotDialog(false);
                    setDuplicateShotName("");
                    setDuplicateInsertIndex(null);
                    
                    // Navigate to the new shot
                    navigate(`/project/${projectId}/studio/${newScene.id}`);
                  }
                } catch (err) {
                  console.error('Duplicate failed:', err);
                  toast.error('Failed to duplicate: ' + (err.response?.data?.detail || err.message));
                } finally {
                  setDuplicatingShot(false);
                }
              }}
              disabled={duplicatingShot || !duplicateShotName.trim() || duplicateInsertIndex === null}
              className="bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="duplicate-shot-confirm-btn"
            >
              {duplicatingShot ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate Shot
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {previewMode && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-4 border-b border-zinc-800 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Preview & Edit Prompt</h2>
              <Button variant="ghost" size="icon" onClick={() => setPreviewMode(false)} className="text-zinc-400">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              {previewImageUrl && <img src={previewImageUrl} alt="Preview" className="w-full rounded-lg mb-4" />}
              <Label className="text-zinc-400 text-sm mb-2 block">Edit prompt before generating:</Label>
              <textarea
                value={previewPrompt}
                onChange={(e) => setPreviewPrompt(e.target.value)}
                className="w-full h-32 bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-sm text-zinc-300 font-mono resize-none"
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setPreviewMode(false)} className="border-zinc-700">Cancel</Button>
                <Button onClick={handleGenerateFromPreview} className="bg-indigo-500 hover:bg-indigo-600">
                  <Wand2 className="w-4 h-4 mr-2" /> Generate Animation
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
